import React, { useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";
import {
  FaEdit,
  FaSearch,
  FaBox,
  FaChevronLeft,
  FaChevronRight,
  FaBuilding,
  FaFilter,
  FaTimesCircle,
} from "react-icons/fa";
import api from "../services/api";
import Footer from "./Footer";
import ModernLoader from "./Loader";
import ProductModal from "./ProductModal";
import ProvisionalInvoiceModal from "./ProvisionalInvoiceModal";
import Header from "./Header";
import "./ProductPage.css";

function ProductPage({ user, onLogout }) {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");

  // Price mode
  const [priceMode, setPriceMode] = useState("W"); // "W" | "X"

  // ✅ Filters: added Catalogue + Design
  const [filters, setFilters] = useState({
    productName: "",
    design: "",
  });

  const [showFilters, setShowFilters] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const [uploading, setUploading] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);

  const fileInputRef = useRef(null);
  const actionMenuRef = useRef(null);

  const itemsPerPage = 12;

  // Lists
  const [productNameList, setProductNameList] = useState([]);

  // ✅ date helpers
  const todayStr = new Date().toISOString().slice(0, 10);

  const toInputDate = (v) => {
    if (!v) return "";
    if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  };

  const formatDate = (v) => {
    if (!v) return "-";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("en-IN");
  };

  // Form (modal) ✅ date included
  const [form, setForm] = useState({
    productName: "",
    designNumber: "",
    date: todayStr,
    wRate: 0,
    xRate: 0,
    isActive: true,
    createdOn: new Date().toISOString(),
    createdBy: user?.username || "Admin",
    modifiedOn: null,
    modifiedBy: null,
  });

  const norm = (v) => (v === null || v === undefined ? "" : String(v)).trim().toLowerCase();

  const getSelectedRate = (item) => {
    const w = Number(item?.wRate ?? 0);
    const x = Number(item?.xRate ?? 0);
    return priceMode === "W" ? (Number.isFinite(w) ? w : 0) : (Number.isFinite(x) ? x : 0);
  };

  const setFilterValue = (key, value) => {
    setSearch("");
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
        setShowActionMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get("/Product");
      const products = res.data?.data || [];
      setData(products);

      const productNames = [...new Set(products.map((p) => p.productName).filter(Boolean))].sort();

      setProductNameList(productNames);
    } catch (err) {
      if (err.response?.status === 401) {
        Swal.fire({
          icon: "warning",
          title: "Session Expired",
          text: "Please login again",
          confirmButtonColor: "#6366f1",
        });
        onLogout();
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: err.response?.data?.message || "Failed to load products",
          confirmButtonColor: "#6366f1",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setForm({
      productName: "",
      designNumber: "",
      date: todayStr,
      wRate: 0,
      xRate: 0,
      isActive: true,
      createdOn: new Date().toISOString(),
      createdBy: user?.username || "Admin",
      modifiedOn: null,
      modifiedBy: null,
    });
    setEditId(null);
  };



  const handleSave = async () => {
    if (!form.productName || !form.designNumber) {
      Swal.fire({
        icon: "warning",
        title: "Required Fields!",
        text: "Please fill Product Name and Design Number",
        confirmButtonColor: "#6366f1",
      });
      return;
    }

    // ✅ future date validation
    if (form.date && form.date > todayStr) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Date",
        text: "Future date allowed nahi hai.",
        confirmButtonColor: "#6366f1",
      });
      return;
    }

    if (isSaving) return;
    setIsSaving(true);

    try {
      const payload = {
        ...form,
        date: form.date ? new Date(`${form.date}T00:00:00.000Z`).toISOString() : null,
        wRate: Number(form.wRate) || 0,
        xRate: Number(form.xRate) || 0,
      };

      if (editId) {
        await api.put(`/Product/${editId}`, {
          ...payload,
          modifiedOn: new Date().toISOString(),
          modifiedBy: user?.username || "Admin",
        });
        Swal.fire({ icon: "success", title: "Updated!", timer: 1200, showConfirmButton: false });
      } else {
        await api.post("/Product", {
          ...payload,
          createdBy: user?.username || "Admin",
        });
        Swal.fire({ icon: "success", title: "Added!", timer: 1200, showConfirmButton: false });
      }

      setShowModal(false);
      resetForm();
      await loadData();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.message || "Operation failed",
        confirmButtonColor: "#6366f1",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (item) => {
    setForm({
      productName: item.productName || "",
      designNumber: item.designNumber || "",
      date: toInputDate(item.date) || todayStr,
      wRate: Number(item.wRate ?? 0),
      xRate: Number(item.xRate ?? 0),
      isActive: item.isActive,
      createdOn: item.createdOn,
      createdBy: item.createdBy,
      modifiedOn: item.modifiedOn,
      modifiedBy: item.modifiedBy,
    });
    setEditId(item.id);
    setShowModal(true);
  };

  const handleUpload = async () => {
    if (!fileInputRef.current?.files?.[0]) {
      Swal.fire({
        icon: "warning",
        title: "No File",
        text: "Please select an Excel file",
        confirmButtonColor: "#6366f1",
      });
      return;
    }

    const file = fileInputRef.current.files[0];
    const formData = new FormData();
    formData.append("file", file);

    setUploading(true);
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      const config = { headers: { "Content-Type": "multipart/form-data" } };
      if (token) config.headers["Authorization"] = `Bearer ${token}`;

      await api.post("/Product/upload-excel", formData, config);

      Swal.fire({
        icon: "success",
        title: "Uploaded!",
        text: "Excel file uploaded successfully",
        timer: 1200,
        showConfirmButton: false,
      });

      fileInputRef.current.value = "";
      setShowActionMenu(false);
      await loadData();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text: err.response?.data?.message || "Failed to upload file.",
        confirmButtonColor: "#6366f1",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleLogout = () => {
    Swal.fire({
      title: "Logout?",
      text: "Are you sure you want to logout?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#6366f1",
      cancelButtonColor: "#ef4444",
      confirmButtonText: "Yes, Logout",
    }).then((result) => {
      if (result.isConfirmed) onLogout();
    });
  };

  const handleClearFilters = () => {
    setFilters({ productName: "", company: "", quality: "", catalogue: "", design: "" });
    setSearch("");
    setCurrentPage(1);
  };

  const filtered = useMemo(() => {
    const s = norm(search);
    const pn = norm(filters.productName);
    const des = norm(filters.design);

    return data.filter((item) => {
      const matchSearch =
        !s ||
        Object.values(item).some((val) => norm(val).includes(s));

      const matchProduct = !pn || norm(item.productName).includes(pn);
      const matchDesign = !des || norm(item.designNumber).includes(des);

      return matchSearch && matchProduct && matchDesign;
    });
  }, [data, search, filters]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
    if (currentPage < 1) setCurrentPage(1);
  }, [currentPage, totalPages]);

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentData = filtered.slice(indexOfFirst, indexOfLast);

  if (loading) {
    return (
      <div className="pp-loadingScreen">
        <ModernLoader isLoading={true} message="Loading Products" />
      </div>
    );
  }

  return (
    <div className="pp-page">
      {/* ✅ loader kept */}
      <ModernLoader
        isLoading={isSaving || uploading}
        message={isSaving ? (editId ? "Updating Product" : "Saving Product") : "Uploading File"}
      />

      <Header
        user={user}
        onLogout={handleLogout}
        onAdd={() => {
          resetForm();
          setShowModal(true);
        }}
        onGenerateBilling={() => setShowInvoice(true)}
        uploading={uploading}
        handleUpload={handleUpload}
        fileInputRef={fileInputRef}
        showActionMenu={showActionMenu}
        setShowActionMenu={setShowActionMenu}
        actionMenuRef={actionMenuRef}
      />

      <main className="pp-container">
        {/* Stats removed */}

        {/* Toolbar */}
        <div className="pp-toolbar">
          <div className="pp-toolbarTop">
            <div className="pp-searchWrap">
              <FaSearch className="pp-searchIcon" />
              <input
                className="pp-input pp-searchInput"
                type="text"
                placeholder="Search all fields..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            <div className="pp-mode">
              <label className="pp-radio">
                <input
                  type="radio"
                  name="priceMode"
                  checked={priceMode === "W"}
                  onChange={() => {
                    setPriceMode("W");
                    setCurrentPage(1);
                  }}
                />
                W Rate
              </label>

              <label className="pp-radio">
                <input
                  type="radio"
                  name="priceMode"
                  checked={priceMode === "X"}
                  onChange={() => {
                    setPriceMode("X");
                    setCurrentPage(1);
                  }}
                />
                X Rate
              </label>
            </div>

          </div>

        </div>

        {/* Meta */}
        <div className="pp-meta">
          <p className="pp-metaText">
            Showing {filtered.length === 0 ? 0 : indexOfFirst + 1}-{Math.min(indexOfLast, filtered.length)} of{" "}
            {filtered.length}
          </p>
          <p className="pp-metaText">
            Mode: <b>{priceMode === "W" ? "W Rate" : "X Rate"}</b>
          </p>
        </div>

        {/* Data */}
        {currentData.length > 0 ? (
          <>
            {/* ✅ Mobile + Tab: BOX view */}
            <div className="pp-grid pp-onlyMobile">
              {currentData.map((p) => (
                <div className={`pp-product ${priceMode === "W" ? "pp-productW" : "pp-productX"}`} key={p.id}>
                  <div className="pp-productHead">
                    <div className="pp-company">
                      {p.productName ? <p className="pp-productName">{p.productName}</p> : null}
                    </div>

                    {/* ✅ only Edit */}
                    <button className="pp-editBtn" onClick={() => handleEdit(p)} type="button">
                      <FaEdit size={12} />
                      Edit
                    </button>
                  </div>

                  {/* ✅ Always 2-2 items in a row */}
                  <div className="pp-kvBox pp-kvBoxAlways2">
                    <div className="pp-kv">
                      <div className="pp-k">C Name / Design Number</div>
                      <div className="pp-v">{p.designNumber || "-"}</div>
                    </div>

                    <div className="pp-kv">
                      <div className="pp-k">Date</div>
                      <div className="pp-v">{formatDate(p.date)}</div>
                    </div>
                  </div>

                  {/* ✅ Only selected rate shown, big + bold + wide, different color */}
                  <div className="pp-foot">
                    <span className={`pp-ratePill ${priceMode === "W" ? "pp-ratePillW" : "pp-ratePillX"}`}>
                      {priceMode === "W" ? "W Rate" : "X Rate"} : ₹{getSelectedRate(p)}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* ✅ Laptop + Desktop: TABLE view */}
            <div className="pp-tableWrap pp-onlyDesktop">
              <table className="pp-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>C Name / Design Number</th>
                    <th>Date</th>
                    <th>{priceMode === "W" ? "W Rate" : "X Rate"}</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {currentData.map((p) => (
                    <tr key={p.id}>
                      <td>{p.productName || "-"}</td>
                      <td>{p.designNumber || "-"}</td>
                      <td>{formatDate(p.date)}</td>
                      <td>
                        <span className={`pp-ratePill ${priceMode === "W" ? "pp-ratePillW" : "pp-ratePillX"}`}>
                          ₹{getSelectedRate(p)}
                        </span>
                      </td>
                      <td>
                        <button className="pp-editBtn" onClick={() => handleEdit(p)} type="button">
                          <FaEdit size={12} />
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="pp-empty">
            <FaBox className="pp-emptyIcon" />
            <p className="pp-emptyText">No products found. Try adjusting filters or search.</p>
          </div>
        )}

        {/* Pagination */}
        {filtered.length > 0 && totalPages > 1 && (
          <div className="pp-pagination">
            <p className="pp-metaText" style={{ margin: 0 }}>
              Page {currentPage} of {totalPages}
            </p>

            <div className="pp-pageBtns">
              <button
                className="pp-pageBtn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                type="button"
              >
                <FaChevronLeft size={11} />
              </button>

              <span className="pp-pagePill">
                {currentPage} / {totalPages}
              </span>

              <button
                className="pp-pageBtn"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                type="button"
              >
                <FaChevronRight size={11} />
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Modals */}
      <ProductModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleSave}
        isSaving={isSaving}
        editId={editId}
        form={form}
        setForm={setForm}
        productNameList={productNameList}
      />

      <ProvisionalInvoiceModal open={showInvoice} onClose={() => setShowInvoice(false)} products={data} user={user} />

      <Footer />
    </div>
  );
}

export default ProductPage;









// import React, { useEffect, useMemo, useRef, useState } from "react";
// import Swal from "sweetalert2";
// import InstallPWA from "./InstallPWA";
// import {
//   FaPlus,
//   FaEdit,
//   FaSignOutAlt,
//   FaSearch,
//   FaBox,
//   FaChevronLeft,
//   FaChevronRight,
//   FaBuilding,
//   FaEllipsisV,
//   FaFilter,
//   FaTimesCircle,
// } from "react-icons/fa";
// import api from "../services/api";
// import Footer from "./Footer";
// import ModernLoader from "./Loader";
// import ProductModal from "./ProductModal";
// import ProvisionalInvoiceModal from "./ProvisionalInvoiceModal";
// import Header from "./Header";
// import "./ProductPage.css";

// function ProductPage({ user, onLogout }) {
//   const [data, setData] = useState([]);
//   const [search, setSearch] = useState("");

//   // Price mode
//   const [priceMode, setPriceMode] = useState("W"); // "W" | "X"

//   // Filters
//   const [filters, setFilters] = useState({
//     productName: "",
//     company: "",
//     quality: "",
//     minPrice: "",
//     maxPrice: "",
//   });

//   const [showFilters, setShowFilters] = useState(false);
//   const [showModal, setShowModal] = useState(false);
//   const [editId, setEditId] = useState(null);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [loading, setLoading] = useState(true);

//   const [uploading, setUploading] = useState(false);
//   const [showActionMenu, setShowActionMenu] = useState(false);
//   const [isSaving, setIsSaving] = useState(false);
//   const [showInvoice, setShowInvoice] = useState(false);

//   const fileInputRef = useRef(null);
//   const actionMenuRef = useRef(null);

//   const itemsPerPage = 12;

//   // Lists
//   const [companyList, setCompanyList] = useState([]);
//   const [shortNameList, setShortNameList] = useState([]);
//   const [catalogueList, setCatalogueList] = useState([]);
//   const [qualityList, setQualityList] = useState([]);
//   const [productNameList, setProductNameList] = useState([]);

//   // Form (modal)
//   const [form, setForm] = useState({
//     productName: "",
//     companyName: "",
//     shortName: "",
//     catalogueName: "",
//     qualityName: "",
//     designNumber: "",
//     wRate: 0,
//     xRate: 0,
//     isActive: true,
//     createdOn: new Date().toISOString(),
//     createdBy: user?.username || "Admin",
//     modifiedOn: null,
//     modifiedBy: null,
//   });

//   const norm = (v) => (v === null || v === undefined ? "" : String(v)).trim().toLowerCase();

//   const getSelectedRate = (item) => {
//     const w = Number(item?.wRate ?? 0);
//     const x = Number(item?.xRate ?? 0);
//     return priceMode === "W" ? (Number.isFinite(w) ? w : 0) : (Number.isFinite(x) ? x : 0);
//   };

//   const setFilterValue = (key, value) => {
//     // ✅ Filter change hote hi search auto-clear
//     setSearch("");
//     setFilters((prev) => ({ ...prev, [key]: value }));
//     setCurrentPage(1);
//   };

//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
//         setShowActionMenu(false);
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);

//   const loadData = async () => {
//     setLoading(true);
//     try {
//       const res = await api.get("/Product");
//       const products = res.data?.data || [];
//       setData(products);

//       const companies = [...new Set(products.map((p) => p.companyName).filter(Boolean))].sort();
//       const shortNames = [...new Set(products.map((p) => p.shortName).filter(Boolean))].sort();
//       const catalogues = [...new Set(products.map((p) => p.catalogueName).filter(Boolean))].sort();
//       const qualities = [...new Set(products.map((p) => p.qualityName).filter(Boolean))].sort();
//       const productNames = [...new Set(products.map((p) => p.productName).filter(Boolean))].sort();

//       setCompanyList(companies);
//       setShortNameList(shortNames);
//       setCatalogueList(catalogues);
//       setQualityList(qualities);
//       setProductNameList(productNames);
//     } catch (err) {
//       if (err.response?.status === 401) {
//         Swal.fire({
//           icon: "warning",
//           title: "Session Expired",
//           text: "Please login again",
//           confirmButtonColor: "#6366f1",
//         });
//         onLogout();
//       } else {
//         Swal.fire({
//           icon: "error",
//           title: "Error",
//           text: err.response?.data?.message || "Failed to load products",
//           confirmButtonColor: "#6366f1",
//         });
//       }
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     loadData();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);

//   const resetForm = () => {
//     setForm({
//       productName: "",
//       companyName: "",
//       shortName: "",
//       catalogueName: "",
//       qualityName: "",
//       designNumber: "",
//       wRate: 0,
//       xRate: 0,
//       isActive: true,
//       createdOn: new Date().toISOString(),
//       createdBy: user?.username || "Admin",
//       modifiedOn: null,
//       modifiedBy: null,
//     });
//     setEditId(null);
//   };

//   const handleCompanyChange = (val) => {
//     setForm((prev) => ({ ...prev, companyName: val }));
//     const existingShortName = data.find((p) => p.companyName === val)?.shortName;
//     if (existingShortName) setForm((prev) => ({ ...prev, shortName: existingShortName }));
//   };

//   const handleSave = async () => {
//     if (!form.productName || !form.companyName || !form.designNumber) {
//       Swal.fire({
//         icon: "warning",
//         title: "Required Fields!",
//         text: "Please fill Product Name, Company Name and Design Number",
//         confirmButtonColor: "#6366f1",
//       });
//       return;
//     }

//     if (isSaving) return;
//     setIsSaving(true);

//     try {
//       const payload = {
//         ...form,
//         wRate: Number(form.wRate) || 0,
//         xRate: Number(form.xRate) || 0,
//       };

//       if (editId) {
//         await api.put(`/Product/${editId}`, {
//           ...payload,
//           modifiedOn: new Date().toISOString(),
//           modifiedBy: user?.username || "Admin",
//         });
//         Swal.fire({ icon: "success", title: "Updated!", timer: 1200, showConfirmButton: false });
//       } else {
//         await api.post("/Product", {
//           ...payload,
//           createdBy: user?.username || "Admin",
//         });
//         Swal.fire({ icon: "success", title: "Added!", timer: 1200, showConfirmButton: false });
//       }

//       setShowModal(false);
//       resetForm();
//       await loadData();
//     } catch (err) {
//       Swal.fire({
//         icon: "error",
//         title: "Error",
//         text: err.response?.data?.message || "Operation failed",
//         confirmButtonColor: "#6366f1",
//       });
//     } finally {
//       setIsSaving(false);
//     }
//   };

//   const handleEdit = (item) => {
//     setForm({
//       productName: item.productName || "",
//       companyName: item.companyName || "",
//       shortName: item.shortName || "",
//       catalogueName: item.catalogueName || "",
//       qualityName: item.qualityName || "",
//       designNumber: item.designNumber || "",
//       wRate: Number(item.wRate ?? 0),
//       xRate: Number(item.xRate ?? 0),
//       isActive: item.isActive,
//       createdOn: item.createdOn,
//       createdBy: item.createdBy,
//       modifiedOn: item.modifiedOn,
//       modifiedBy: item.modifiedBy,
//     });
//     setEditId(item.id);
//     setShowModal(true);
//   };

//   const handleUpload = async () => {
//     if (!fileInputRef.current?.files?.[0]) {
//       Swal.fire({
//         icon: "warning",
//         title: "No File",
//         text: "Please select an Excel file",
//         confirmButtonColor: "#6366f1",
//       });
//       return;
//     }

//     const file = fileInputRef.current.files[0];
//     const formData = new FormData();
//     formData.append("file", file);

//     setUploading(true);
//     try {
//       const token = localStorage.getItem("token") || sessionStorage.getItem("token");
//       const config = { headers: { "Content-Type": "multipart/form-data" } };
//       if (token) config.headers["Authorization"] = `Bearer ${token}`;

//       await api.post("/Product/upload-excel", formData, config);

//       Swal.fire({
//         icon: "success",
//         title: "Uploaded!",
//         text: "Excel file uploaded successfully",
//         timer: 1200,
//         showConfirmButton: false,
//       });

//       fileInputRef.current.value = "";
//       setShowActionMenu(false);
//       await loadData();
//     } catch (err) {
//       Swal.fire({
//         icon: "error",
//         title: "Upload Failed",
//         text: err.response?.data?.message || "Failed to upload file.",
//         confirmButtonColor: "#6366f1",
//       });
//     } finally {
//       setUploading(false);
//     }
//   };

//   const handleLogout = () => {
//     Swal.fire({
//       title: "Logout?",
//       text: "Are you sure you want to logout?",
//       icon: "question",
//       showCancelButton: true,
//       confirmButtonColor: "#6366f1",
//       cancelButtonColor: "#ef4444",
//       confirmButtonText: "Yes, Logout",
//     }).then((result) => {
//       if (result.isConfirmed) onLogout();
//     });
//   };

//   const handleClearFilters = () => {
//     setFilters({ productName: "", company: "", quality: "", minPrice: "", maxPrice: "" });
//     setSearch("");
//     setCurrentPage(1);
//   };

//   // ✅ Bug-free filtering (safe normalize + safe numeric parsing)
//   const filtered = useMemo(() => {
//     const s = norm(search);
//     const pn = norm(filters.productName);
//     const comp = norm(filters.company);
//     const qual = norm(filters.quality);

//     const minParsed = filters.minPrice === "" ? null : Number(filters.minPrice);
//     const maxParsed = filters.maxPrice === "" ? null : Number(filters.maxPrice);
//     const min = minParsed !== null && Number.isFinite(minParsed) ? minParsed : null;
//     const max = maxParsed !== null && Number.isFinite(maxParsed) ? maxParsed : null;

//     return data.filter((item) => {
//       const matchSearch =
//         !s ||
//         Object.values(item).some((val) => {
//           const text = norm(val);
//           return text.includes(s);
//         });

//       const matchProduct = !pn || norm(item.productName).includes(pn);
//       const matchCompany = !comp || norm(item.companyName) === comp;
//       const matchQuality = !qual || norm(item.qualityName) === qual;

//       const rate = getSelectedRate(item);
//       const matchPrice = (min === null || rate >= min) && (max === null || rate <= max);

//       return matchSearch && matchProduct && matchCompany && matchQuality && matchPrice;
//     });
//   }, [data, search, filters, priceMode]);

//   const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));

//   // ✅ If filters reduce total pages, keep currentPage valid
//   useEffect(() => {
//     if (currentPage > totalPages) setCurrentPage(totalPages);
//     if (currentPage < 1) setCurrentPage(1);
//   }, [currentPage, totalPages]);

//   const indexOfLast = currentPage * itemsPerPage;
//   const indexOfFirst = indexOfLast - itemsPerPage;
//   const currentData = filtered.slice(indexOfFirst, indexOfLast);

//   if (loading) {
//     return (
//       <div className="pp-loadingScreen">
//         <ModernLoader isLoading={true} message="Loading Products" />
//       </div>
//     );
//   }

//   return (
//     <div className="pp-page">
//       <ModernLoader
//         isLoading={isSaving || uploading}
//         message={isSaving ? (editId ? "Updating Product" : "Saving Product") : "Uploading File"}
//       />

//       {/* Header */}
//      <Header
//   user={user}
//   onLogout={handleLogout}
//   onAdd={() => {
//     resetForm();
//     setShowModal(true);
//   }}
//   onGenerateBilling={() => setShowInvoice(true)}
//   uploading={uploading}
//   handleUpload={handleUpload}
//   fileInputRef={fileInputRef}
//   showActionMenu={showActionMenu}
//   setShowActionMenu={setShowActionMenu}
//   actionMenuRef={actionMenuRef}
// />
     

//       {/* Main */}
//       <main className="pp-container">
//         {/* Stats */}
//         <div className="pp-statsRow">
//           <div className="pp-statCard">
//             <div className="pp-statIcon pp-statIconBlue">
//               <FaBox />
//             </div>
//             <div>
//               <p className="pp-statLabel">Total Products</p>
//               <p className="pp-statValue">{data.length}</p>
//             </div>
//           </div>

//           <div className="pp-statCard">
//             <div className="pp-statIcon pp-statIconGreen">
//               <FaBuilding />
//             </div>
//             <div>
//               <p className="pp-statLabel">Companies</p>
//               <p className="pp-statValue">{companyList.length}</p>
//             </div>
//           </div>
//         </div>

//         {/* Toolbar */}
//         <div className="pp-toolbar">
//           <div className="pp-toolbarTop">
//             <div className="pp-searchWrap">
//               <FaSearch className="pp-searchIcon" />
//               <input
//                 className="pp-input pp-searchInput"
//                 type="text"
//                 placeholder="Search all fields..."
//                 value={search}
//                 onChange={(e) => {
//                   setSearch(e.target.value);
//                   setCurrentPage(1);
//                 }}
//               />
//             </div>

//             <div className="pp-mode">
//               <label className="pp-radio">
//                 <input
//                   type="radio"
//                   name="priceMode"
//                   checked={priceMode === "W"}
//                   onChange={() => {
//                     setPriceMode("W");
//                     setCurrentPage(1);
//                   }}
//                 />
//                 W Rate
//               </label>
//               <label className="pp-radio">
//                 <input
//                   type="radio"
//                   name="priceMode"
//                   checked={priceMode === "X"}
//                   onChange={() => {
//                     setPriceMode("X");
//                     setCurrentPage(1);
//                   }}
//                 />
//                 X Rate
//               </label>
//             </div>

//             <button className="pp-filterBtn" onClick={() => setShowFilters((s) => !s)} type="button">
//               <FaFilter size={12} />
//               Filters
//             </button>

//             <button className="pp-clearBtn" onClick={handleClearFilters} type="button">
//               <FaTimesCircle size={12} />
//               Clear
//             </button>
//           </div>

//           {/* Filters panel */}
//           {showFilters && (
//             <div className="pp-filters">
//               {/* ✅ Product Name → Searchable Dropdown (datalist) */}
//               <div className="pp-field">
//                 <label className="pp-label">Product Name</label>
//                 <input
//                   className="pp-input"
//                   list="ppProductNameList"
//                   placeholder="Type / select product..."
//                   value={filters.productName}
//                   onChange={(e) => setFilterValue("productName", e.target.value)}
//                 />
//                 <datalist id="ppProductNameList">
//                   {productNameList.map((name) => (
//                     <option key={name} value={name} />
//                   ))}
//                 </datalist>
//               </div>

//               <div className="pp-field">
//                 <label className="pp-label">Company</label>
//                 <select
//                   className="pp-select"
//                   value={filters.company}
//                   onChange={(e) => setFilterValue("company", e.target.value)}
//                 >
//                   <option value="">All Companies</option>
//                   {companyList.map((c) => (
//                     <option key={c} value={c}>
//                       {c}
//                     </option>
//                   ))}
//                 </select>
//               </div>

//               <div className="pp-field">
//                 <label className="pp-label">Quality</label>
//                 <select
//                   className="pp-select"
//                   value={filters.quality}
//                   onChange={(e) => setFilterValue("quality", e.target.value)}
//                 >
//                   <option value="">All Qualities</option>
//                   {qualityList.map((q) => (
//                     <option key={q} value={q}>
//                       {q}
//                     </option>
//                   ))}
//                 </select>
//               </div>

//               <div className="pp-field">
//                 <label className="pp-label">Min Price ({priceMode})</label>
//                 <input
//                   className="pp-input"
//                   type="number"
//                   inputMode="decimal"
//                   placeholder="Min"
//                   value={filters.minPrice}
//                   onChange={(e) => setFilterValue("minPrice", e.target.value)}
//                 />
//               </div>

//               <div className="pp-field">
//                 <label className="pp-label">Max Price ({priceMode})</label>
//                 <input
//                   className="pp-input"
//                   type="number"
//                   inputMode="decimal"
//                   placeholder="Max"
//                   value={filters.maxPrice}
//                   onChange={(e) => setFilterValue("maxPrice", e.target.value)}
//                 />
//               </div>
//             </div>
//           )}
//         </div>

//         {/* Meta */}
//         <div className="pp-meta">
//           <p className="pp-metaText">
//             Showing {filtered.length === 0 ? 0 : indexOfFirst + 1}-{Math.min(indexOfLast, filtered.length)} of{" "}
//             {filtered.length}
//           </p>
//           <p className="pp-metaText">
//             Mode: <b>{priceMode === "W" ? "W Rate" : "X Rate"}</b>
//           </p>
//         </div>

//         {/* ✅ Responsive GRID: Mobile 1, Tablet 2, Desktop 3 (handled by your CSS .pp-grid) */}
//         {currentData.length > 0 ? (
//           <div className="pp-grid">
//             {currentData.map((p) => (
//               <div className="pp-product" key={p.id}>
//                 <div className="pp-productHead">
//                   <div className="pp-company">
//                     <p className="pp-companyName" title={p.companyName || "-"}>
//                       {p.companyName || "-"}{" "}
//                       {p.shortName ? <span className="pp-companyShort">({p.shortName})</span> : null}
//                     </p>
//                     {p.productName ? <p className="pp-productName">{p.productName}</p> : null}
//                   </div>

//                   <button className="pp-editBtn" onClick={() => handleEdit(p)} type="button">
//                     <FaEdit size={12} />
//                     Edit
//                   </button>
//                 </div>

//                 <div className="pp-kvBox">
//                   <div className="pp-kv">
//                     <div className="pp-k">Catalogue</div>
//                     <div className="pp-v">{p.catalogueName || "-"}</div>
//                   </div>

//                   <div className="pp-kv">
//                     <div className="pp-k">Quality</div>
//                     <div className="pp-v">{p.qualityName || "-"}</div>
//                   </div>

//                   <div className="pp-kv pp-span2">
//                     <div className="pp-k">Design</div>
//                     <div className="pp-v">{p.designNumber || "-"}</div>
//                   </div>
//                 </div>

//                 <div className="pp-foot">
//                   <span className="pp-rate">
//                     {priceMode === "W" ? "W Rate" : "X Rate"} : ₹{getSelectedRate(p)}
//                   </span>
//                 </div>
//               </div>
//             ))}
//           </div>
//         ) : (
//           <div className="pp-empty">
//             <FaBox className="pp-emptyIcon" />
//             <p className="pp-emptyText">No products found. Try adjusting filters or search.</p>
//           </div>
//         )}

//         {/* Pagination */}
//         {filtered.length > 0 && totalPages > 1 && (
//           <div className="pp-pagination">
//             <p className="pp-metaText" style={{ margin: 0 }}>
//               Page {currentPage} of {totalPages}
//             </p>

//             <div className="pp-pageBtns">
//               <button
//                 className="pp-pageBtn"
//                 disabled={currentPage === 1}
//                 onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
//                 type="button"
//               >
//                 <FaChevronLeft size={11} />
//               </button>

//               <span className="pp-pagePill">
//                 {currentPage} / {totalPages}
//               </span>

//               <button
//                 className="pp-pageBtn"
//                 disabled={currentPage === totalPages}
//                 onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
//                 type="button"
//               >
//                 <FaChevronRight size={11} />
//               </button>
//             </div>
//           </div>
//         )}
//       </main>

//       {/* Modals */}
//       <ProductModal
//         open={showModal}
//         onClose={() => setShowModal(false)}
//         onSave={handleSave}
//         isSaving={isSaving}
//         editId={editId}
//         form={form}
//         setForm={setForm}
//         companyList={companyList}
//         shortNameList={shortNameList}
//         catalogueList={catalogueList}
//         qualityList={qualityList}
//         productNameList={productNameList}
//         handleCompanyChange={handleCompanyChange}
//       />

//       <ProvisionalInvoiceModal
//         open={showInvoice}
//         onClose={() => setShowInvoice(false)}
//         products={data}
//         user={user}
//         onSave={async (payload) => {
//           // return await api.post("/Invoice", payload);
//         }}
//       />

//       <Footer />
//     </div>
//   );
// }

// export default ProductPage;



// import React, { useEffect, useState, useRef } from "react";
// import Swal from "sweetalert2";
// import InstallPWA from "./InstallPWA";
// import {
//   FaPlus,
//   FaEdit,
//   FaSignOutAlt,
//   FaSearch,
//   FaBox,
//   FaChevronLeft,
//   FaChevronRight,
//   FaBars,
//   FaBuilding,
//   FaEllipsisV,
//   FaFilter,
//   FaTimesCircle,
// } from "react-icons/fa";
// import api from "../services/api";
// import Footer from "./Footer";
// import ModernLoader from "./Loader";
// import ProductModal from "./ProductModal"; // ✅ NEW
// import ProvisionalInvoiceModal from "./ProvisionalInvoiceModal";

// function ProductPage({ user, onLogout }) {
//   const [data, setData] = useState([]);
//   const [search, setSearch] = useState("");

//   // ✅ Radio selection for price display/filter
//   const [priceMode, setPriceMode] = useState("W"); // "W" | "X"

//   const [filters, setFilters] = useState({
//     company: "",
//     quality: "",
//     minPrice: "",
//     maxPrice: "",
//   });

//   const [showFilters, setShowFilters] = useState(false);
//   const [showModal, setShowModal] = useState(false);
//   const [editId, setEditId] = useState(null);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [loading, setLoading] = useState(true);
//   const [showMobileMenu, setShowMobileMenu] = useState(false);
//   const [uploading, setUploading] = useState(false);
//   const [showActionMenu, setShowActionMenu] = useState(false);
//   const [isSaving, setIsSaving] = useState(false);
// const [showInvoice, setShowInvoice] = useState(false);
//   const fileInputRef = useRef(null);
//   const actionMenuRef = useRef(null);
//   const itemsPerPage = 12;

//   const [companyList, setCompanyList] = useState([]);
//   const [shortNameList, setShortNameList] = useState([]);
//   const [catalogueList, setCatalogueList] = useState([]);
//   const [qualityList, setQualityList] = useState([]);

//   // ✅ Date removed
//   const [form, setForm] = useState({
//     productName: "",
//     companyName: "",
//     shortName: "",
//     catalogueName: "",
//     qualityName: "",
//     designNumber: "",
//     wRate: 0,
//     xRate: 0,
//     isActive: true,
//     createdOn: new Date().toISOString(),
//     createdBy: user?.username || "Admin",
//     modifiedOn: null,
//     modifiedBy: null,
//   });

//   const getSelectedRate = (item) => {
//     const w = Number(item?.wRate ?? 0);
//     const x = Number(item?.xRate ?? 0);
//     return priceMode === "W" ? w : x;
//   };


  

//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
//         setShowActionMenu(false);
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);

//   const loadData = async () => {
//     setLoading(true);
//     try {
//       const res = await api.get("/Product");
//       const products = res.data.data || [];
//       setData(products);

//       const companies = [...new Set(products.map((p) => p.companyName).filter(Boolean))];
//       const shortNames = [...new Set(products.map((p) => p.shortName).filter(Boolean))];
//       const catalogues = [...new Set(products.map((p) => p.catalogueName).filter(Boolean))];
//       const qualities = [...new Set(products.map((p) => p.qualityName).filter(Boolean))];

//       setCompanyList(companies.sort());
//       setShortNameList(shortNames.sort());
//       setCatalogueList(catalogues.sort());
//       setQualityList(qualities.sort());
//     } catch (err) {
//       if (err.response?.status === 401) {
//         Swal.fire({
//           icon: "warning",
//           title: "Session Expired",
//           text: "Please login again",
//           confirmButtonColor: "#6366f1",
//         });
//         onLogout();
//       }
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     loadData();
//   }, []);

//   const filtered = data.filter((item) => {
//     const matchSearch = Object.values(item).some((val) =>
//       val?.toString().toLowerCase().includes(search.toLowerCase())
//     );

//     const matchCompany = !filters.company || item.companyName === filters.company;
//     const matchQuality = !filters.quality || item.qualityName === filters.quality;

//     const selectedRate = getSelectedRate(item);
//     const min = filters.minPrice === "" ? null : parseFloat(filters.minPrice);
//     const max = filters.maxPrice === "" ? null : parseFloat(filters.maxPrice);

//     const matchPrice = (min === null || selectedRate >= min) && (max === null || selectedRate <= max);

//     return matchSearch && matchCompany && matchQuality && matchPrice;
//   });

//   const totalPages = Math.ceil(filtered.length / itemsPerPage);
//   const indexOfLast = currentPage * itemsPerPage;
//   const indexOfFirst = indexOfLast - itemsPerPage;
//   const currentData = filtered.slice(indexOfFirst, indexOfLast);

//   const resetForm = () => {
//     setForm({
//       productName: "",
//       companyName: "",
//       shortName: "",
//       catalogueName: "",
//       qualityName: "",
//       designNumber: "",
//       wRate: 0,
//       xRate: 0,
//       isActive: true,
//       createdOn: new Date().toISOString(),
//       createdBy: user?.username || "Admin",
//       modifiedOn: null,
//       modifiedBy: null,
//     });
//     setEditId(null);
//   };

//   const handleCompanyChange = (val) => {
//     setForm((prev) => ({ ...prev, companyName: val }));

//     const existingShortName = data.find((p) => p.companyName === val)?.shortName;
//     if (existingShortName) {
//       setForm((prev) => ({ ...prev, shortName: existingShortName }));
//     }
//   };

//   const handleSave = async () => {
//     if (!form.productName || !form.companyName || !form.designNumber) {
//       Swal.fire({
//         icon: "warning",
//         title: "Required Fields!",
//         text: "Please fill Product Name, Company Name and Design Number",
//         confirmButtonColor: "#6366f1",
//       });
//       return;
//     }

//     if (isSaving) return;
//     setIsSaving(true);

//     try {
//       const payload = {
//         ...form,
//         wRate: Number(form.wRate) || 0,
//         xRate: Number(form.xRate) || 0,
//       };

//       if (editId) {
//         await api.put(`/Product/${editId}`, {
//           ...payload,
//           modifiedOn: new Date().toISOString(),
//           modifiedBy: user?.username || "Admin",
//         });

//         Swal.fire({
//           icon: "success",
//           title: "Updated!",
//           text: "Product updated successfully",
//           timer: 1500,
//           showConfirmButton: false,
//         });
//       } else {
//         await api.post("/Product", {
//           ...payload,
//           createdBy: user?.username || "Admin",
//         });

//         Swal.fire({
//           icon: "success",
//           title: "Added!",
//           text: "Product added successfully",
//           timer: 1500,
//           showConfirmButton: false,
//         });
//       }

//       setShowModal(false);
//       resetForm();
//       await loadData();
//     } catch (err) {
//       Swal.fire({
//         icon: "error",
//         title: "Error",
//         text: err.response?.data?.message || "Operation failed",
//         confirmButtonColor: "#6366f1",
//       });
//     } finally {
//       setIsSaving(false);
//     }
//   };

//   const handleEdit = (item) => {
//     setForm({
//       productName: item.productName || "",
//       companyName: item.companyName || "",
//       shortName: item.shortName || "",
//       catalogueName: item.catalogueName || "",
//       qualityName: item.qualityName || "",
//       designNumber: item.designNumber || "",
//       wRate: Number(item.wRate ?? 0),
//       xRate: Number(item.xRate ?? 0),
//       isActive: item.isActive,
//       createdOn: item.createdOn,
//       createdBy: item.createdBy,
//       modifiedOn: item.modifiedOn,
//       modifiedBy: item.modifiedBy,
//     });
//     setEditId(item.id);
//     setShowModal(true);
//   };

//   const handleUpload = async () => {
//     if (!fileInputRef.current?.files?.[0]) {
//       Swal.fire({
//         icon: "warning",
//         title: "No File",
//         text: "Please select an Excel file",
//         confirmButtonColor: "#6366f1",
//       });
//       return;
//     }

//     const file = fileInputRef.current.files[0];
//     const formData = new FormData();
//     formData.append("file", file);

//     setUploading(true);
//     try {
//       const token = localStorage.getItem("token") || sessionStorage.getItem("token");
//       const config = { headers: { "Content-Type": "multipart/form-data" } };
//       if (token) config.headers["Authorization"] = `Bearer ${token}`;

//       await api.post("/Product/upload-excel", formData, config);

//       Swal.fire({
//         icon: "success",
//         title: "Success!",
//         text: "Excel file uploaded successfully",
//         timer: 1500,
//         showConfirmButton: false,
//       });

//       fileInputRef.current.value = "";
//       setShowActionMenu(false);
//       await loadData();
//     } catch (err) {
//       Swal.fire({
//         icon: "error",
//         title: "Upload Failed",
//         text: err.response?.data?.message || "Failed to upload file. Please check file format.",
//         confirmButtonColor: "#6366f1",
//       });
//     } finally {
//       setUploading(false);
//     }
//   };

//   const handleLogout = () => {
//     Swal.fire({
//       title: "Logout?",
//       text: "Are you sure you want to logout?",
//       icon: "question",
//       showCancelButton: true,
//       confirmButtonColor: "#6366f1",
//       cancelButtonColor: "#ef4444",
//       confirmButtonText: "Yes, Logout",
//     }).then((result) => {
//       if (result.isConfirmed) onLogout();
//     });
//   };

//   const handleFilterChange = (newFilters) => {
//     setFilters(newFilters);
//     setSearch("");
//     setCurrentPage(1);
//   };

//   const handleClearFilters = () => {
//     setFilters({ company: "", quality: "", minPrice: "", maxPrice: "" });
//     setSearch("");
//     setCurrentPage(1);
//   };

//   if (loading) {
//     return (
//       <div
//         style={{
//           minHeight: "100vh",
//           background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
//           display: "flex",
//           alignItems: "center",
//           justifyContent: "center",
//           padding: "20px",
//         }}
//       >
//         <ModernLoader isLoading={true} message="Loading Products" />
//       </div>
//     );
//   }

//   return (
//     <div style={{ background: "#f9fafb", minHeight: "100vh", paddingBottom: "40px" }}>
//       <ModernLoader
//         isLoading={isSaving || uploading}
//         message={isSaving ? (editId ? "Updating Product" : "Saving Product") : "Uploading File"}
//       />

//       {/* Header */}
//       <header
//         style={{
//           background: "white",
//           borderBottom: "1px solid #e5e7eb",
//           position: "sticky",
//           top: 0,
//           zIndex: 100,
//         }}
//       >
//         <div
//           style={{
//             maxWidth: "1400px",
//             margin: "0 auto",
//             padding: "16px 20px",
//             display: "flex",
//             justifyContent: "space-between",
//             alignItems: "center",
//           }}
//         >
//           <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
//             <img
//               src="/mtlogo.jpg"
//               alt="MT"
//               style={{ height: "40px", width: "auto", maxWidth: "160px", objectFit: "contain" }}
//             />
//             <div>
//               <h1 style={{ margin: 0, fontSize: "20px", fontWeight: 700, color: "#111827" }}>
//                 Products
//               </h1>
//             </div>
//           </div>

//           <div
//             style={{ position: "relative", display: "flex", gap: "12px", alignItems: "center" }}
//             ref={actionMenuRef}
//           >
//             <button
//               onClick={() => {
//                 resetForm();
//                 setShowModal(true);
//               }}
//               style={{
//                 background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
//                 color: "white",
//                 border: "none",
//                 borderRadius: "8px",
//                 padding: "8px 16px",
//                 fontWeight: 600,
//                 cursor: "pointer",
//                 fontSize: "13px",
//                 display: "flex",
//                 alignItems: "center",
//                 gap: "6px",
//               }}
//             >
//               <FaPlus size={12} />
//             </button>

//             <button
//               onClick={(e) => {
//                 e.stopPropagation();
//                 setShowActionMenu(!showActionMenu);
//               }}
//               style={{
//                 background: "#f3f4f6",
//                 border: "1px solid #e5e7eb",
//                 borderRadius: "8px",
//                 padding: "8px 12px",
//                 cursor: "pointer",
//                 fontSize: "18px",
//                 color: "#374151",
//                 display: "flex",
//                 alignItems: "center",
//                 justifyContent: "center",
//               }}
//             >
//               <FaEllipsisV />
//             </button>

//             {showActionMenu && (
//               <div
//                 onClick={(e) => e.stopPropagation()}
//                 style={{
//                   position: "absolute",
//                   top: "100%",
//                   right: 0,
//                   marginTop: "8px",
//                   background: "white",
//                   border: "1px solid #e5e7eb",
//                   borderRadius: "8px",
//                   boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
//                   minWidth: "240px",
//                   zIndex: 1000,
//                 }}
//               >
//                 <div
//                   style={{
//                     padding: "12px 16px",
//                     borderBottom: "1px solid #e5e7eb",
//                     fontSize: "12px",
//                     color: "#6b7280",
//                   }}
//                 >
//                   <p style={{ margin: 0, fontWeight: 600, color: "#374151" }}>{user?.username}</p>
//                   <p style={{ margin: "2px 0 0", fontSize: "11px" }}>Account</p>
//                 </div>

//                 <InstallPWA />
//                 <button
//   onClick={(e) => {
//     e.stopPropagation();
//     setShowActionMenu(false);   // menu close
//     setShowInvoice(true);       // invoice modal open
//   }}
//   style={{
//     width: "100%",
//     padding: "10px 16px",
//     background: "transparent",
//     border: "none",
//     color: "#374151",
//     fontSize: "13px",
//     fontWeight: "500",
//     cursor: "pointer",
//     display: "flex",
//     alignItems: "center",
//     gap: "10px",
//     borderBottom: "1px solid #e5e7eb",
//     transition: "all 0.2s",
//   }}
//   onMouseEnter={(e) => (e.currentTarget.style.background = "#f3f4f6")}
//   onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
// >
//   <span style={{ fontSize: "14px" }}>🧾</span>
//   <span>Generate Billing</span>
// </button>

//                 <label
//                   onClick={(e) => e.stopPropagation()}
//                   style={{
//                     display: "flex",
//                     alignItems: "center",
//                     gap: "10px",
//                     padding: "10px 16px",
//                     cursor: uploading ? "not-allowed" : "pointer",
//                     color: uploading ? "#9ca3af" : "#374151",
//                     fontSize: "13px",
//                     fontWeight: 500,
//                     borderBottom: "1px solid #e5e7eb",
//                     opacity: uploading ? 0.6 : 1,
//                   }}
//                 >
//                   <span style={{ fontSize: "14px" }}>{uploading ? "🔄" : "🆙"}</span>
//                   <span>{uploading ? "Uploading..." : "Import Excel File"}</span>
//                   <input
//                     ref={fileInputRef}
//                     type="file"
//                     accept=".xlsx,.xls,.csv"
//                     onChange={() => fileInputRef.current?.files?.[0] && handleUpload()}
//                     disabled={uploading}
//                     style={{ display: "none" }}
//                   />
//                 </label>

//                 <button
//                   onClick={(e) => {
//                     e.stopPropagation();
//                     setShowActionMenu(false);
//                     handleLogout();
//                   }}
//                   style={{
//                     width: "100%",
//                     padding: "10px 16px",
//                     background: "transparent",
//                     border: "none",
//                     color: "#ef4444",
//                     fontSize: "13px",
//                     fontWeight: 500,
//                     cursor: "pointer",
//                     display: "flex",
//                     alignItems: "center",
//                     gap: "10px",
//                   }}
//                 >
//                   <FaSignOutAlt size={13} />
//                   <span>Logout</span>
//                 </button>
//               </div>
//             )}

//             <button
//               onClick={() => setShowMobileMenu(!showMobileMenu)}
//               style={{
//                 background: "transparent",
//                 border: "none",
//                 fontSize: "18px",
//                 color: "#374151",
//                 cursor: "pointer",
//                 display: "none",
//               }}
//             >
//               <FaBars />
//             </button>
//           </div>
//         </div>
//       </header>

//       {/* Main */}
//       <main style={{ maxWidth: "1400px", margin: "0 auto", padding: "24px 20px" }}>
//         {/* Stats */}
//         <div
//           style={{
//             display: "grid",
//             gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
//             gap: "16px",
//             marginBottom: "24px",
//           }}
//         >
//           <div
//             style={{
//               background: "white",
//               borderRadius: "12px",
//               padding: "20px",
//               border: "1px solid #e5e7eb",
//               display: "flex",
//               alignItems: "center",
//               gap: "16px",
//             }}
//           >
//             <div
//               style={{
//                 width: "50px",
//                 height: "50px",
//                 background: "#ecf0ff",
//                 borderRadius: "10px",
//                 display: "flex",
//                 alignItems: "center",
//                 justifyContent: "center",
//                 color: "#6366f1",
//                 fontSize: "24px",
//               }}
//             >
//               <FaBox />
//             </div>
//             <div>
//               <p style={{ margin: 0, fontSize: "16px", color: "#6e48a8", fontWeight: "bold" }}>
//                 Total Products
//               </p>
//               <h3 style={{ margin: "4px 0 0", fontSize: "28px", fontWeight: 700, color: "#111827" }}>
//                 {data.length}
//               </h3>
//             </div>
//           </div>

//           <div
//             style={{
//               background: "white",
//               borderRadius: "12px",
//               padding: "20px",
//               border: "1px solid #e5e7eb",
//               display: "flex",
//               alignItems: "center",
//               gap: "16px",
//             }}
//           >
//             <div
//               style={{
//                 width: "50px",
//                 height: "50px",
//                 background: "#ecfdf5",
//                 borderRadius: "10px",
//                 display: "flex",
//                 alignItems: "center",
//                 justifyContent: "center",
//                 color: "#10b981",
//                 fontSize: "24px",
//               }}
//             >
//               <FaBuilding />
//             </div>
//             <div>
//               <p style={{ margin: 0, fontSize: "16px", color: "#6e48a8", fontWeight: "bold" }}>
//                 Companies
//               </p>
//               <h3 style={{ margin: "4px 0 0", fontSize: "28px", fontWeight: 700, color: "#111827" }}>
//                 {companyList.length}
//               </h3>
//             </div>
//           </div>
//         </div>

//         {/* Search + Price Mode + Filters */}
//         <div
//           style={{
//             background: "white",
//             borderRadius: "12px",
//             padding: "16px",
//             border: "1px solid #e5e7eb",
//             marginBottom: "24px",
//           }}
//         >
//           <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "16px" }}>
//             <div style={{ flex: 1, minWidth: "200px", position: "relative" }}>
//               <FaSearch
//                 size={14}
//                 style={{
//                   position: "absolute",
//                   left: "12px",
//                   top: "50%",
//                   transform: "translateY(-50%)",
//                   color: "#9ca3af",
//                 }}
//               />
//               <input
//                 type="text"
//                 placeholder="Search products..."
//                 value={search}
//                 onChange={(e) => {
//                   setSearch(e.target.value);
//                   setCurrentPage(1);
//                 }}
//                 style={{
//                   width: "100%",
//                   padding: "10px 12px 10px 40px",
//                   border: "2px solid #e5e7eb",
//                   borderRadius: "8px",
//                   fontSize: "14px",
//                   outline: "none",
//                   boxSizing: "border-box",
//                 }}
//               />
//             </div>

//             {/* ✅ Radio (W Rate / X Rate) */}
//             <div
//               style={{
//                 display: "flex",
//                 alignItems: "center",
//                 gap: "16px",
//                 padding: "6px 10px",
//                 border: "1px solid #e5e7eb",
//                 borderRadius: "8px",
//                 background: "#fff",
//               }}
//             >
//               <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "#374151", cursor: "pointer" }}>
//                 <input
//                   type="radio"
//                   name="priceMode"
//                   value="W"
//                   checked={priceMode === "W"}
//                   onChange={() => {
//                     setPriceMode("W");
//                     setCurrentPage(1);
//                   }}
//                 />
//                 W Rate
//               </label>

//               <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "#374151", cursor: "pointer" }}>
//                 <input
//                   type="radio"
//                   name="priceMode"
//                   value="X"
//                   checked={priceMode === "X"}
//                   onChange={() => {
//                     setPriceMode("X");
//                     setCurrentPage(1);
//                   }}
//                 />
//                 X Rate
//               </label>
//             </div>

//             <button
//               onClick={(e) => {
//                 e.stopPropagation();
//                 setShowFilters(!showFilters);
//               }}
//               style={{
//                 background: showFilters ? "#6366f1" : "#f3f4f6",
//                 color: showFilters ? "white" : "#374151",
//                 border: "1px solid " + (showFilters ? "#6366f1" : "#e5e7eb"),
//                 borderRadius: "8px",
//                 padding: "10px 16px",
//                 fontWeight: 600,
//                 cursor: "pointer",
//                 fontSize: "13px",
//                 display: "flex",
//                 alignItems: "center",
//                 gap: "6px",
//               }}
//             >
//               <FaFilter size={12} />
//               Filters
//             </button>
//           </div>

//           {showFilters && (
//             <div
//               onClick={(e) => e.stopPropagation()}
//               style={{
//                 background: "#f9fafb",
//                 border: "1px solid #e5e7eb",
//                 borderRadius: "8px",
//                 padding: "16px",
//                 display: "grid",
//                 gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
//                 gap: "12px",
//               }}
//             >
//               <div>
//                 <label style={{ fontSize: "12px", fontWeight: 600, color: "#374151", display: "block", marginBottom: "6px" }}>
//                   Company
//                 </label>
//                 <select
//                   value={filters.company}
//                   onChange={(e) => handleFilterChange({ ...filters, company: e.target.value })}
//                   style={{
//                     width: "100%",
//                     padding: "8px 12px",
//                     border: "1px solid #d1d5db",
//                     borderRadius: "6px",
//                     fontSize: "13px",
//                     cursor: "pointer",
//                     background: "white",
//                   }}
//                 >
//                   <option value="">All Companies</option>
//                   {companyList.map((c) => (
//                     <option key={c} value={c}>
//                       {c}
//                     </option>
//                   ))}
//                 </select>
//               </div>

//               <div>
//                 <label style={{ fontSize: "12px", fontWeight: 600, color: "#374151", display: "block", marginBottom: "6px" }}>
//                   Quality
//                 </label>
//                 <select
//                   value={filters.quality}
//                   onChange={(e) => handleFilterChange({ ...filters, quality: e.target.value })}
//                   style={{
//                     width: "100%",
//                     padding: "8px 12px",
//                     border: "1px solid #d1d5db",
//                     borderRadius: "6px",
//                     fontSize: "13px",
//                     cursor: "pointer",
//                     background: "white",
//                   }}
//                 >
//                   <option value="">All Qualities</option>
//                   {qualityList.map((q) => (
//                     <option key={q} value={q}>
//                       {q}
//                     </option>
//                   ))}
//                 </select>
//               </div>

//               <div>
//                 <label style={{ fontSize: "12px", fontWeight: 600, color: "#374151", display: "block", marginBottom: "6px" }}>
//                   Min Price ({priceMode === "W" ? "W Rate" : "X Rate"})
//                 </label>
//                 <input
//                   type="number"
//                   placeholder="Min"
//                   value={filters.minPrice}
//                   onChange={(e) => handleFilterChange({ ...filters, minPrice: e.target.value })}
//                   style={{
//                     width: "100%",
//                     padding: "8px 12px",
//                     border: "1px solid #d1d5db",
//                     borderRadius: "6px",
//                     fontSize: "13px",
//                     outline: "none",
//                     boxSizing: "border-box",
//                   }}
//                 />
//               </div>

//               <div>
//                 <label style={{ fontSize: "12px", fontWeight: 600, color: "#374151", display: "block", marginBottom: "6px" }}>
//                   Max Price ({priceMode === "W" ? "W Rate" : "X Rate"})
//                 </label>
//                 <input
//                   type="number"
//                   placeholder="Max"
//                   value={filters.maxPrice}
//                   onChange={(e) => handleFilterChange({ ...filters, maxPrice: e.target.value })}
//                   style={{
//                     width: "100%",
//                     padding: "8px 12px",
//                     border: "1px solid #d1d5db",
//                     borderRadius: "6px",
//                     fontSize: "13px",
//                     outline: "none",
//                     boxSizing: "border-box",
//                   }}
//                 />
//               </div>

//               <div style={{ display: "flex", alignItems: "flex-end" }}>
//                 <button
//                   onClick={(e) => {
//                     e.stopPropagation();
//                     handleClearFilters();
//                   }}
//                   style={{
//                     width: "100%",
//                     background: "#f3f4f6",
//                     border: "1px solid #d1d5db",
//                     borderRadius: "6px",
//                     padding: "8px 12px",
//                     fontSize: "12px",
//                     fontWeight: 600,
//                     color: "#374151",
//                     cursor: "pointer",
//                     display: "flex",
//                     alignItems: "center",
//                     justifyContent: "center",
//                     gap: "4px",
//                   }}
//                 >
//                   <FaTimesCircle size={12} />
//                   Clear
//                 </button>
//               </div>
//             </div>
//           )}
//         </div>

//         {/* Products Grid */}
//         {currentData.length > 0 ? (
//           <div
//             style={{
//               display: "grid",
//               gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
//               gap: "16px",
//               marginBottom: "24px",
//             }}
//           >
//             {currentData.map((p) => (
//               <div
//                 key={p.id}
//                 style={{
//                   background: "white",
//                   borderRadius: "12px",
//                   border: "1px solid #e5e7eb",
//                   padding: "16px",
//                 }}
//               >
//                 <div style={{ marginBottom: "12px" }}>
//                   <h4 style={{ margin: "0 0 4px", fontSize: "15px", fontWeight: 800, color: "#111827" }}>
//                     {p.productName || "-"}
//                   </h4>

//                   <p style={{ margin: 0, fontSize: "13px", color: "#374151", fontWeight: 700 }}>
//                     {p.companyName}
//                     <span style={{ marginLeft: "10px", color: "#6b7280", fontWeight: 600, fontSize: "12px" }}>
//                       {p.shortName ? `(${p.shortName})` : ""}
//                     </span>
//                   </p>
//                 </div>

//                 <div
//                   style={{
//                     display: "grid",
//                     gridTemplateColumns: "1fr 1fr",
//                     gap: "12px",
//                     marginBottom: "12px",
//                     paddingBottom: "12px",
//                     borderBottom: "1px solid #e5e7eb",
//                   }}
//                 >
//                   <div>
//                     <p style={{ margin: "0 0 4px", fontSize: "11px", color: "#6b7280", fontWeight: 500 }}>CATALOGUE</p>
//                     <p style={{ margin: 0, fontSize: "13px", fontWeight: 600, color: "#374151" }}>
//                       {p.catalogueName || "-"}
//                     </p>
//                   </div>

//                   <div>
//                     <p style={{ margin: "0 0 4px", fontSize: "11px", color: "#6b7280", fontWeight: 500 }}>QUALITY</p>
//                     <p style={{ margin: 0, fontSize: "13px", fontWeight: 600, color: "#374151" }}>
//                       {p.qualityName || "-"}
//                     </p>
//                   </div>

//                   <div>
//                     <p style={{ margin: "0 0 4px", fontSize: "11px", color: "#6b7280", fontWeight: 500 }}>DESIGN</p>
//                     <span
//                       style={{
//                         display: "inline-block",
//                         background: "#f3f4f6",
//                         color: "#374151",
//                         padding: "4px 8px",
//                         borderRadius: "6px",
//                         fontSize: "12px",
//                         fontWeight: 600,
//                       }}
//                     >
//                       {p.designNumber}
//                     </span>
//                   </div>

//                   {/* ✅ DATE removed completely */}
//                 </div>

//                 <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
//                   <span
//                     style={{
//                       background: "#ecfdf5",
//                       color: "#047857",
//                       padding: "6px 12px",
//                       borderRadius: "6px",
//                       fontSize: "16px",
//                       fontWeight: "bold",
//                     }}
//                   >
//                     {priceMode === "W" ? "W Rate" : "X Rate"} : ₹{getSelectedRate(p)}
//                   </span>

//                   <button
//                     onClick={() => handleEdit(p)}
//                     style={{
//                       background: "#f3f4f6",
//                       color: "#374151",
//                       border: "1px solid #d1d5db",
//                       borderRadius: "6px",
//                       padding: "6px 12px",
//                       fontWeight: 600,
//                       cursor: "pointer",
//                       fontSize: "12px",
//                       display: "flex",
//                       alignItems: "center",
//                       gap: "6px",
//                     }}
//                   >
//                     <FaEdit size={11} />
//                     Edit
//                   </button>
//                 </div>
//               </div>
//             ))}
//           </div>
//         ) : (
//           <div
//             style={{
//               background: "white",
//               borderRadius: "12px",
//               border: "1px solid #e5e7eb",
//               padding: "60px 20px",
//               textAlign: "center",
//             }}
//           >
//             <FaBox style={{ fontSize: "48px", color: "#d1d5db", marginBottom: "16px" }} />
//             <p style={{ color: "#6b7280", margin: 0, fontSize: "14px" }}>
//               No products found. Try adjusting your filters or search.
//             </p>
//           </div>
//         )}

//         {/* Pagination */}
//         {totalPages > 1 && (
//           <div
//             style={{
//               display: "flex",
//               justifyContent: "space-between",
//               alignItems: "center",
//               marginTop: "24px",
//               flexWrap: "wrap",
//               gap: "16px",
//             }}
//           >
//             <p style={{ margin: 0, fontSize: "13px", color: "#6b7280" }}>
//               Showing {indexOfFirst + 1}-{Math.min(indexOfLast, filtered.length)} of {filtered.length}
//             </p>
//             <div style={{ display: "flex", gap: "8px" }}>
//               <button
//                 disabled={currentPage === 1}
//                 onClick={() => setCurrentPage(currentPage - 1)}
//                 style={{
//                   background: currentPage === 1 ? "#f3f4f6" : "white",
//                   color: currentPage === 1 ? "#9ca3af" : "#374151",
//                   border: "1px solid #e5e7eb",
//                   borderRadius: "6px",
//                   padding: "8px 12px",
//                   cursor: currentPage === 1 ? "not-allowed" : "pointer",
//                   fontSize: "12px",
//                   fontWeight: 500,
//                 }}
//               >
//                 <FaChevronLeft size={11} />
//               </button>

//               <span
//                 style={{
//                   background: "#6366f1",
//                   color: "white",
//                   padding: "8px 12px",
//                   borderRadius: "6px",
//                   fontWeight: 600,
//                   fontSize: "12px",
//                 }}
//               >
//                 {currentPage} / {totalPages}
//               </span>

//               <button
//                 disabled={currentPage === totalPages}
//                 onClick={() => setCurrentPage(currentPage + 1)}
//                 style={{
//                   background: currentPage === totalPages ? "#f3f4f6" : "white",
//                   color: currentPage === totalPages ? "#9ca3af" : "#374151",
//                   border: "1px solid #e5e7eb",
//                   borderRadius: "6px",
//                   padding: "8px 12px",
//                   cursor: currentPage === totalPages ? "not-allowed" : "pointer",
//                   fontSize: "12px",
//                   fontWeight: 500,
//                 }}
//               >
//                 <FaChevronRight size={11} />
//               </button>
//             </div>
//           </div>
//         )}
//       </main>

//       {/* ✅ Modal component used here */}
//       <ProductModal
//         open={showModal}
//         onClose={() => setShowModal(false)}
//         onSave={handleSave}
//         isSaving={isSaving}
//         editId={editId}
//         form={form}
//         setForm={setForm}
//         companyList={companyList}
//         shortNameList={shortNameList}
//         catalogueList={catalogueList}
//         qualityList={qualityList}
//         handleCompanyChange={handleCompanyChange}
//       />

// <ProvisionalInvoiceModal
//   open={showInvoice}
//   onClose={() => setShowInvoice(false)}
//   products={data}
//   user={user}
//   onSave={async (payload) => {
//     // example:
//     // return await api.post("/Invoice", payload);
//   }}
// />


//       <Footer />
//     </div>
//   );
// }

// export default ProductPage;





// import React, { useEffect, useState, useRef } from "react";
// import Swal from "sweetalert2";
// import InstallPWA from "./InstallPWA";   // ← Yeh line add karo
// import {
//   FaPlus,
//   FaEdit,
//   FaSignOutAlt,
//   FaSearch,
//   FaBox,
//   FaUser,
//   FaChevronLeft,
//   FaChevronRight,
//   FaTimes,
//   FaSave,
//   FaBars,
//   FaBuilding,
//   FaChevronDown,
//   FaCheck,
//   FaEllipsisV,
//   FaFilter,
//   FaTimesCircle,
// } from "react-icons/fa";
// import api from "../services/api";
// import Footer from "./Footer";

// // ✅ ComboBox Component
// const ComboBox = ({
//   label,
//   value,
//   onChange,
//   options = [],
//   placeholder,
//   required = false,
//   icon: Icon,
// }) => {
//   const [isOpen, setIsOpen] = useState(false);
//   const [searchText, setSearchText] = useState(value || "");
//   const [filteredOptions, setFilteredOptions] = useState(options);
//   const wrapperRef = useRef(null);

//   useEffect(() => {
//     setSearchText(value || "");
//   }, [value]);

//   useEffect(() => {
//     if (searchText) {
//       const filtered = options.filter((opt) =>
//         opt.toLowerCase().includes(searchText.toLowerCase())
//       );
//       setFilteredOptions(filtered);
//     } else {
//       setFilteredOptions(options);
//     }
//   }, [searchText, options]);

//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
//         setIsOpen(false);
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);

//   const handleInputChange = (e) => {
//     const val = e.target.value;
//     setSearchText(val);
//     onChange(val);
//     setIsOpen(true);
//   };

//   const handleSelect = (opt) => {
//     setSearchText(opt);
//     onChange(opt);
//     setIsOpen(false);
//   };

//   const isNewValue = searchText && !options.includes(searchText);

//   return (
//     <div style={{ marginBottom: "16px" }} ref={wrapperRef}>
//       <label
//         style={{
//           display: "flex",
//           alignItems: "center",
//           gap: "8px",
//           color: "#374151",
//           fontSize: "13px",
//           fontWeight: "600",
//           marginBottom: "6px",
//         }}
//       >
//         {Icon && <Icon size={14} style={{ color: "#6366f1" }} />}
//         {label}
//         {required && <span style={{ color: "#ef4444" }}>*</span>}
//       </label>

//       <div style={{ position: "relative" }}>
//         <input
//           type="text"
//           placeholder={placeholder || `Select or type ${label}`}
//           value={searchText}
//           onChange={handleInputChange}
//           onFocus={() => setIsOpen(true)}
//           style={{
//             width: "100%",
//             padding: "10px 36px 10px 12px",
//             border: isNewValue ? "2px solid #10b981" : "2px solid #d1d5db",
//             borderRadius: "8px",
//             fontSize: "14px",
//             backgroundColor: "#fff",
//             outline: "none",
//             transition: "all 0.2s",
//             boxSizing: "border-box",
//           }}
//           // onFocus={(e) => (e.target.style.borderColor = "#6366f1")}
//           onBlur={(e) => (e.target.style.borderColor = isNewValue ? "#10b981" : "#d1d5db")}
//         />

//         <FaChevronDown
//           size={12}
//           style={{
//             position: "absolute",
//             right: "12px",
//             top: "50%",
//             transform: `translateY(-50%) rotate(${isOpen ? "180deg" : "0deg"})`,
//             color: "#6b7280",
//             transition: "transform 0.2s",
//             pointerEvents: "none",
//           }}
//         />

//         {isNewValue && (
//           <span
//             style={{
//               position: "absolute",
//               top: "-10px",
//               right: "10px",
//               background: "#10b981",
//               color: "white",
//               padding: "2px 8px",
//               borderRadius: "6px",
//               fontSize: "10px",
//               fontWeight: "600",
//             }}
//           >
//             + NEW
//           </span>
//         )}

//         {isOpen && (
//           <div
//             style={{
//               position: "absolute",
//               top: "100%",
//               left: 0,
//               right: 0,
//               background: "white",
//               border: "2px solid #e5e7eb",
//               borderRadius: "8px",
//               boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
//               maxHeight: "220px",
//               overflowY: "auto",
//               zIndex: 1000,
//               marginTop: "4px",
//             }}
//           >
//             {isNewValue && (
//               <div
//                 onClick={() => handleSelect(searchText)}
//                 style={{
//                   padding: "10px 12px",
//                   cursor: "pointer",
//                   background: "#ecfdf5",
//                   borderBottom: "1px solid #e5e7eb",
//                   display: "flex",
//                   alignItems: "center",
//                   gap: "8px",
//                   color: "#047857",
//                   fontSize: "13px",
//                   fontWeight: "500",
//                 }}
//               >
//                 <FaPlus size={11} />
//                 Add "{searchText}"
//               </div>
//             )}

//             {filteredOptions.length > 0 ? (
//               filteredOptions.map((opt, idx) => (
//                 <div
//                   key={idx}
//                   onClick={() => handleSelect(opt)}
//                   style={{
//                     padding: "10px 12px",
//                     cursor: "pointer",
//                     background: opt === searchText ? "#f3f4f6" : "white",
//                     borderBottom: idx < filteredOptions.length - 1 ? "1px solid #f3f4f6" : "none",
//                     display: "flex",
//                     alignItems: "center",
//                     justifyContent: "space-between",
//                     fontSize: "13px",
//                     transition: "background 0.15s",
//                   }}
//                   onMouseEnter={(e) => (e.currentTarget.style.background = "#f9fafb")}
//                   onMouseLeave={(e) =>
//                     (e.currentTarget.style.background = opt === searchText ? "#f3f4f6" : "white")
//                   }
//                 >
//                   <span style={{ color: "#374151" }}>{opt}</span>
//                   {opt === searchText && <FaCheck size={11} style={{ color: "#6366f1" }} />}
//                 </div>
//               ))
//             ) : !isNewValue ? (
//               <div style={{ padding: "12px", textAlign: "center", color: "#9ca3af", fontSize: "13px" }}>
//                 No options found
//               </div>
//             ) : null}
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// // ✅ Main Product Page Component
// function ProductPage({ user, onLogout }) {
//   const [data, setData] = useState([]);
//   const [search, setSearch] = useState("");
//   const [filters, setFilters] = useState({
//     company: "",
//     quality: "",
//     minPrice: "",
//     maxPrice: "",
//   });
//   const [showFilters, setShowFilters] = useState(false);
//   const [showModal, setShowModal] = useState(false);
//   const [editId, setEditId] = useState(null);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [loading, setLoading] = useState(true);
//   const [showMobileMenu, setShowMobileMenu] = useState(false);
//   const [uploading, setUploading] = useState(false);
//   const [companyLogo, setCompanyLogo] = useState(null);
//   const [showActionMenu, setShowActionMenu] = useState(false);

//   const fileInputRef = useRef(null);
//   const actionMenuRef = useRef(null);
//   const itemsPerPage = 12;

//   const [companyList, setCompanyList] = useState([]);
//   const [shortNameList, setShortNameList] = useState([]);
//   const [catalogueList, setCatalogueList] = useState([]);
//   const [qualityList, setQualityList] = useState([]);

//   const [form, setForm] = useState({
//     companyName: "",
//     shortName: "",
//     catalogueName: "",
//     qualityName: "",
//     designNumber: "",
//     rate: 0,
//     date: new Date().toISOString().split("T")[0],
//     isActive: true,
//     createdOn: new Date().toISOString(),
//     createdBy: user?.username || "Admin",
//     modifiedOn: null,
//     modifiedBy: null,
//   });

//   // Close menu when clicking outside
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
//         setShowActionMenu(false);
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);

//   const loadData = async () => {
//     setLoading(true);
//     try {
//       const res = await api.get("/Product");
//       const products = res.data.data || [];
//       setData(products);

//       const companies = [...new Set(products.map((p) => p.companyName).filter(Boolean))];
//       const shortNames = [...new Set(products.map((p) => p.shortName).filter(Boolean))];
//       const catalogues = [...new Set(products.map((p) => p.catalogueName).filter(Boolean))];
//       const qualities = [...new Set(products.map((p) => p.qualityName).filter(Boolean))];

//       setCompanyList(companies.sort());
//       setShortNameList(shortNames.sort());
//       setCatalogueList(catalogues.sort());
//       setQualityList(qualities.sort());
//     } catch (err) {
//       if (err.response?.status === 401) {
//         Swal.fire({
//           icon: "warning",
//           title: "Session Expired",
//           text: "Please login again",
//           confirmButtonColor: "#6366f1",
//         });
//         onLogout();
//       }
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     loadData();
//     const savedLogo = localStorage.getItem("companyLogo");
//     if (savedLogo) {
//       setCompanyLogo(savedLogo);
//     }
//   }, []);

//   // Filter and search
//   const filtered = data.filter((item) => {
//     const matchSearch = Object.values(item).some((val) =>
//       val?.toString().toLowerCase().includes(search.toLowerCase())
//     );

//     const matchCompany = !filters.company || item.companyName === filters.company;
//     const matchQuality = !filters.quality || item.qualityName === filters.quality;
//     const matchPrice =
//       (!filters.minPrice || item.rate >= parseFloat(filters.minPrice)) &&
//       (!filters.maxPrice || item.rate <= parseFloat(filters.maxPrice));

//     return matchSearch && matchCompany && matchQuality && matchPrice;
//   });

//   const totalPages = Math.ceil(filtered.length / itemsPerPage);
//   const indexOfLast = currentPage * itemsPerPage;
//   const indexOfFirst = indexOfLast - itemsPerPage;
//   const currentData = filtered.slice(indexOfFirst, indexOfLast);

//   const resetForm = () => {
//     setForm({
//       companyName: "",
//       shortName: "",
//       catalogueName: "",
//       qualityName: "",
//       designNumber: "",
//       rate: 0,
//       date: new Date().toISOString().split("T")[0],
//       isActive: true,
//       createdOn: new Date().toISOString(),
//       createdBy: user?.username || "Admin",
//       modifiedOn: null,
//       modifiedBy: null,
//     });
//     setEditId(null);
//   };

//   const handleSave = async () => {
//     if (!form.companyName || !form.designNumber) {
//       Swal.fire({
//         icon: "warning",
//         title: "Required Fields!",
//         text: "Please fill Company Name and Design Number",
//         confirmButtonColor: "#6366f1",
//       });
//       return;
//     }

//     try {
//       if (editId) {
//         await api.put(`/Product/${editId}`, {
//           ...form,
//           modifiedOn: new Date().toISOString(),
//           modifiedBy: user?.username || "Admin",
//         });
//         Swal.fire({
//           icon: "success",
//           title: "Updated!",
//           text: "Product updated successfully",
//           timer: 1500,
//           showConfirmButton: false,
//         });
//       } else {
//         await api.post("/Product", {
//           ...form,
//           createdBy: user?.username || "Admin",
//         });
//         Swal.fire({
//           icon: "success",
//           title: "Added!",
//           text: "Product added successfully",
//           timer: 1500,
//           showConfirmButton: false,
//         });
//       }
//       setShowModal(false);
//       resetForm();
//       loadData();
//     } catch (err) {
//       Swal.fire({
//         icon: "error",
//         title: "Error",
//         text: "Operation failed",
//         confirmButtonColor: "#6366f1",
//       });
//     }
//   };

//   const handleEdit = (item) => {
//     setForm({
//       companyName: item.companyName || "",
//       shortName: item.shortName || "",
//       catalogueName: item.catalogueName || "",
//       qualityName: item.qualityName || "",
//       designNumber: item.designNumber || "",
//       rate: item.rate || 0,
//       date: item.date?.split("T")[0] || new Date().toISOString().split("T")[0],
//       isActive: item.isActive,
//       createdOn: item.createdOn,
//       createdBy: item.createdBy,
//       modifiedOn: item.modifiedOn,
//       modifiedBy: item.modifiedBy,
//     });
//     setEditId(item.id);
//     setShowModal(true);
//   };

//   const handleUpload = async () => {
//     if (!fileInputRef.current?.files?.[0]) {
//       Swal.fire({
//         icon: "warning",
//         title: "No File",
//         text: "Please select an Excel file",
//         confirmButtonColor: "#6366f1",
//       });
//       return;
//     }

//     const file = fileInputRef.current.files[0];
//     const formData = new FormData();
//     formData.append("file", file);

//     setUploading(true);

//     try {
//       // Get JWT token
//       const token = localStorage.getItem("token") || sessionStorage.getItem("token");

//       const config = {
//         headers: {
//           "Content-Type": "multipart/form-data",
//         },
//       };

//       // Add JWT token if exists
//       if (token) {
//         config.headers["Authorization"] = `Bearer ${token}`;
//       }

//       const response = await api.post("/Product/upload-excel", formData, config);

//       Swal.fire({
//         icon: "success",
//         title: "Success!",
//         text: "Excel file uploaded successfully",
//         timer: 1500,
//         showConfirmButton: false,
//       });

//       fileInputRef.current.value = "";
//       setShowActionMenu(false);
//       await loadData();
//     } catch (err) {
//       console.error("Upload error:", err);
//       Swal.fire({
//         icon: "error",
//         title: "Upload Failed",
//         text: err.response?.data?.message || "Failed to upload file. Please check file format.",
//         confirmButtonColor: "#6366f1",
//       });
//     } finally {
//       setUploading(false);
//     }
//   };

//   const handleLogout = () => {
//     Swal.fire({
//       title: "Logout?",
//       text: "Are you sure you want to logout?",
//       icon: "question",
//       showCancelButton: true,
//       confirmButtonColor: "#6366f1",
//       cancelButtonColor: "#ef4444",
//       confirmButtonText: "Yes, Logout",
//     }).then((result) => {
//       if (result.isConfirmed) {
//         onLogout();
//       }
//     });
//   };

//   // const handleLogoUpload = (e) => {
//   //   const file = e.target.files[0];
//   //   if (file) {
//   //     const reader = new FileReader();
//   //     reader.onload = (event) => {
//   //       setCompanyLogo(event.target.result);
//   //       localStorage.setItem("companyLogo", event.target.result);
//   //     };
//   //     reader.readAsDataURL(file);
//   //   }
//   // };

//   if (loading) {
//     return (
//       <div
//         style={{
//           minHeight: "100vh",
//           background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
//           display: "flex",
//           alignItems: "center",
//           justifyContent: "center",
//           padding: "20px",
//         }}
//       >
//         <div style={{ textAlign: "center" }}>
//           <div
//             style={{
//               width: "60px",
//               height: "60px",
//               border: "4px solid rgba(255,255,255,0.3)",
//               borderTop: "4px solid white",
//               borderRadius: "50%",
//               animation: "spin 1s linear infinite",
//               margin: "0 auto 20px",
//             }}
//           />
//           <h4 style={{ color: "white", margin: "0", fontWeight: "600" }}>
//             Loading Products...
//           </h4>
//           <style>{`
//             @keyframes spin {
//               from { transform: rotate(0deg); }
//               to { transform: rotate(360deg); }
//             }
//           `}</style>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div style={{ background: "#f9fafb", minHeight: "100vh", paddingBottom: "40px" }}>
//       {/* Header */}
//       <header
//         style={{
//           background: "white",
//           borderBottom: "1px solid #e5e7eb",
//           position: "sticky",
//           top: 0,
//           zIndex: 100,
//         }}
//       >
//         <div
//           style={{
//             maxWidth: "1400px",
//             margin: "0 auto",
//             padding: "16px 20px",
//             display: "flex",
//             justifyContent: "space-between",
//             alignItems: "center",
//           }}
//         >
//           {/* Logo & Title */}
//           {/* Logo & Title */}
// <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
//   <img
//     src="/mtlogo.jpg" // <-- logo from public folder
//     alt="MT"
//     style={{
//       height: "40px",
//       width: "auto",
//       maxWidth: "160px",
//       objectFit: "contain",
//     }}
//   />
//   <div>
//     <h1
//       style={{
//         margin: "0",
//         fontSize: "20px",
//         fontWeight: "700",
//         color: "#111827",
//       }}
//     >
//       Products
//     </h1>
//     <p
//       style={{
//         margin: "2px 0 0",
//         fontSize: "12px",
//         color: "#6b7280",
//       }}
//     >
//       {/* Manage MT Price inventory */}
//     </p>
//   </div>
// </div>
//           {/* <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
//             {companyLogo ? (
//               <img
//                 src={companyLogo}
//                 alt="Company Logo"
//                 style={{
//                   height: "40px",
//                   width: "auto",
//                   maxWidth: "160px",
//                   objectFit: "contain",
//                 }}
//               />
//             ) : (
//               <div
//                 style={{
//                   width: "40px",
//                   height: "40px",
//                   background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
//                   borderRadius: "10px",
//                   display: "flex",
//                   alignItems: "center",
//                   justifyContent: "center",
//                   color: "white",
//                   fontSize: "22px",
//                   fontWeight: "bold",
//                 }}
//               >
//                 P
//               </div>
//             )}
//             <div>
//               <h1
//                 style={{
//                   margin: "0",
//                   fontSize: "20px",
//                   fontWeight: "700",
//                   color: "#111827",
//                 }}
//               >
//                 Products
//               </h1>
//               <p
//                 style={{
//                   margin: "2px 0 0",
//                   fontSize: "12px",
//                   color: "#6b7280",
//                 }}
//               >
//                 Manage MT Price inventory
//               </p>
//             </div>
//           </div> */}

//           {/* Action Menu */}
//          <div style={{ position: "relative", display: "flex", gap: "12px", alignItems: "center" }} ref={actionMenuRef}>

//   {/* Add Product Button */}
//   <button
//     onClick={() => {
//       resetForm();
//       setShowModal(true);
//     }}
//     style={{
//       background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
//       color: "white",
//       border: "none",
//       borderRadius: "8px",
//       padding: "8px 16px",
//       fontWeight: "600",
//       cursor: "pointer",
//       fontSize: "13px",
//       display: "flex",
//       alignItems: "center",
//       gap: "6px",
//       transition: "all 0.2s",
//     }}
//     onMouseEnter={(e) => {
//       e.target.style.transform = "translateY(-2px)";
//       e.target.style.boxShadow = "0 8px 16px rgba(99, 102, 241, 0.3)";
//     }}
//     onMouseLeave={(e) => {
//       e.target.style.transform = "translateY(0)";
//       e.target.style.boxShadow = "none";
//     }}
//   >
//     <FaPlus size={12} />
//     <span style={{ display: "none" }}>Add</span>
//   </button>

//   {/* Menu Button */}
//   <button
//     onClick={() => setShowActionMenu(!showActionMenu)}
//     style={{
//       background: "#f3f4f6",
//       border: "1px solid #e5e7eb",
//       borderRadius: "8px",
//       padding: "8px 12px",
//       cursor: "pointer",
//       fontSize: "18px",
//       color: "#374151",
//       display: "flex",
//       alignItems: "center",
//       justifyContent: "center",
//       transition: "all 0.2s",
//     }}
//     onMouseEnter={(e) => (e.target.style.background = "#e5e7eb")}
//     onMouseLeave={(e) => (e.target.style.background = "#f3f4f6")}
//   >
//     <FaEllipsisV />
//   </button>

//   {/* Dropdown Menu */}
//   {showActionMenu && (
//     <div
//       style={{
//         position: "absolute",
//         top: "100%",
//         right: "0",
//         marginTop: "8px",
//         background: "white",
//         border: "1px solid #e5e7eb",
//         borderRadius: "8px",
//         boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
//         minWidth: "240px",
//         zIndex: 1000,
//       }}
//     >
//       {/* User Info */}
//       <div
//         style={{
//           padding: "12px 16px",
//           borderBottom: "1px solid #e5e7eb",
//           fontSize: "12px",
//           color: "#6b7280",
//         }}
//       >
//         <p style={{ margin: "0", fontWeight: "600", color: "#374151" }}>
//           {user?.username}
//         </p>
//         <p style={{ margin: "2px 0 0", fontSize: "11px" }}>Account</p>
//       </div>

//       {/* ✅ Install App (PWA) - Naya Item */}
//       <InstallPWA />

//       {/* Upload File */}
//       <label
//         style={{
//           display: "flex",
//           alignItems: "center",
//           gap: "10px",
//           padding: "10px 16px",
//           cursor: uploading ? "not-allowed" : "pointer",
//           color: uploading ? "#9ca3af" : "#374151",
//           fontSize: "13px",
//           fontWeight: "500",
//           borderBottom: "1px solid #e5e7eb",
//           transition: "all 0.2s",
//           opacity: uploading ? 0.6 : 1,
//         }}
//         onMouseEnter={(e) => {
//           if (!uploading) e.currentTarget.style.background = "#f3f4f6";
//         }}
//         onMouseLeave={(e) => {
//           e.currentTarget.style.background = "transparent";
//         }}
//       >
//         {uploading ? (
//           <>
//             <div
//               style={{
//                 width: "12px",
//                 height: "12px",
//                 border: "2px solid #e5e7eb",
//                 borderTop: "2px solid #6366f1",
//                 borderRadius: "50%",
//                 animation: "spin 0.8s linear infinite",
//               }}
//             />
//             <span>Uploading...</span>
//           </>
//         ) : (
//           <>
//             <span style={{ fontSize: "14px" }}>🆙</span>
//             <span>Import Excel File</span>
//           </>
//         )}
//         <input
//           ref={fileInputRef}
//           type="file"
//           accept=".xlsx,.xls,.csv"
//           onChange={() => {
//             if (fileInputRef.current?.files?.[0]) {
//               handleUpload();
//             }
//           }}
//           disabled={uploading}
//           style={{ display: "none" }}
//         />
//       </label>

//       {/* Logout */}
//       <button
//         onClick={() => {
//           setShowActionMenu(false);
//           handleLogout();
//         }}
//         style={{
//           width: "100%",
//           padding: "10px 16px",
//           background: "transparent",
//           border: "none",
//           color: "#ef4444",
//           fontSize: "13px",
//           fontWeight: "500",
//           cursor: "pointer",
//           display: "flex",
//           alignItems: "center",
//           gap: "10px",
//           transition: "all 0.2s",
//         }}
//         onMouseEnter={(e) => (e.target.style.background = "#fee2e2")}
//         onMouseLeave={(e) => (e.target.style.background = "transparent")}
//       >
//         <FaSignOutAlt size={13} />
//         <span>Logout</span>
//       </button>
//     </div>
//   )}

//   {/* Mobile Menu Button */}
//   <button
//     onClick={() => setShowMobileMenu(!showMobileMenu)}
//     style={{
//       background: "transparent",
//       border: "none",
//       fontSize: "18px",
//       color: "#374151",
//       cursor: "pointer",
//       display: "none",
//     }}
//   >
//     <FaBars />
//   </button>
// </div>
//         </div>

//         {/* Mobile Menu */}
//         {showMobileMenu && (
//           <div
//             style={{
//               background: "#f9fafb",
//               borderTop: "1px solid #e5e7eb",
//               padding: "12px 20px",
//               display: "flex",
//               flexDirection: "column",
//               gap: "12px",
//             }}
//           >
//             <div style={{ fontSize: "13px", color: "#6b7280" }}>
//               Logged in as <strong>{user?.username}</strong>
//             </div>

//             <label
//               style={{
//                 display: "flex",
//                 alignItems: "center",
//                 gap: "10px",
//                 padding: "10px 12px",
//                 cursor: uploading ? "not-allowed" : "pointer",
//                 color: uploading ? "#9ca3af" : "#374151",
//                 fontSize: "13px",
//                 fontWeight: "500",
//                 background: "#ecfdf5",
//                 borderRadius: "8px",
//                 border: "1px solid #d1fae5",
//                 opacity: uploading ? 0.6 : 1,
//               }}
//             >
//               {uploading ? (
//                 <>
//                   <div
//                     style={{
//                       width: "12px",
//                       height: "12px",
//                       border: "2px solid #d1fae5",
//                       borderTop: "2px solid #10b981",
//                       borderRadius: "50%",
//                       animation: "spin 0.8s linear infinite",
//                     }}
//                   />
//                   <span>Uploading...</span>
//                 </>
//               ) : (
//                 <>
//                   <span style={{ fontSize: "14px" }}>📤</span>
//                   <span>Import Excel</span>
//                 </>
//               )}
//               <input
//                 ref={fileInputRef}
//                 type="file"
//                 accept=".xlsx,.xls,.csv"
//                 onChange={() => {
//                   if (fileInputRef.current?.files?.[0]) {
//                     handleUpload();
//                   }
//                 }}
//                 disabled={uploading}
//                 style={{ display: "none" }}
//               />
//             </label>

//             {/* <label
//               style={{
//                 display: "flex",
//                 alignItems: "center",
//                 gap: "10px",
//                 padding: "10px 12px",
//                 cursor: "pointer",
//                 color: "#374151",
//                 fontSize: "13px",
//                 fontWeight: "500",
//                 background: "#eef2ff",
//                 borderRadius: "8px",
//                 border: "1px solid #c7d2fe",
//               }}
//             >
//               <span style={{ fontSize: "14px" }}>🖼️</span>
//               <span>Upload Logo</span>
//               <input
//                 type="file"
//                 accept="image/*"
//                 onChange={(e) => {
//                   handleLogoUpload(e);
//                   setShowMobileMenu(false);
//                 }}
//                 style={{ display: "none" }}
//               />
//             </label> */}

//             <button
//               onClick={() => {
//                 setShowMobileMenu(false);
//                 handleLogout();
//               }}
//               style={{
//                 background: "#ef4444",
//                 color: "white",
//                 border: "none",
//                 borderRadius: "8px",
//                 padding: "8px 12px",
//                 fontWeight: "600",
//                 cursor: "pointer",
//                 fontSize: "13px",
//                 display: "flex",
//                 alignItems: "center",
//                 justifyContent: "center",
//                 gap: "6px",
//               }}
//             >
//               <FaSignOutAlt size={12} />
//               Logout
//             </button>
//           </div>
//         )}
//       </header>

//       {/* Main Content */}
//       <main
//         style={{
//           maxWidth: "1400px",
//           margin: "0 auto",
//           padding: "24px 20px",
//         }}
//       >
//         {/* Stats Cards */}
//         <div
//           style={{
//             display: "grid",
//             gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
//             gap: "16px",
//             marginBottom: "24px",
//           }}
//         >
//           <div
//             style={{
//               background: "white",
//               borderRadius: "12px",
//               padding: "20px",
//               border: "1px solid #e5e7eb",
//               display: "flex",
//               alignItems: "center",
//               gap: "16px",
//             }}
//           >
//             <div
//               style={{
//                 width: "50px",
//                 height: "50px",
//                 background: "#ecf0ff",
//                 borderRadius: "10px",
//                 display: "flex",
//                 alignItems: "center",
//                 justifyContent: "center",
//                 color: "#6366f1",
//                 fontSize: "24px",
//               }}
//             >
//               <FaBox />
//             </div>
//             <div>
//               <p style={{ margin: "0", fontSize: "16px", color: "#6e48a8", fontWeight:"bold" }}>
//                 Total Products
//               </p>
//               <h3 style={{ margin: "4px 0 0", fontSize: "28px", fontWeight: "700", color: "#111827" }}>
//                 {data.length}
//               </h3>
//             </div>
//           </div>

//           <div
//             style={{
//               background: "white",
//               borderRadius: "12px",
//               padding: "20px",
//               border: "1px solid #e5e7eb",
//               display: "flex",
//               alignItems: "center",
//               gap: "16px",
//             }}
//           >
//             <div
//               style={{
//                 width: "50px",
//                 height: "50px",
//                 background: "#ecfdf5",
//                 borderRadius: "10px",
//                 display: "flex",
//                 alignItems: "center",
//                 justifyContent: "center",
//                 color: "#10b981",
//                 fontSize: "24px",
//               }}
//             >
//               <FaBuilding />
//             </div>
//             <div>
//               <p style={{ margin: "0", fontSize: "16px", color: "#6e48a8",  fontWeight:"bold"}}>
//                 Companies
//               </p>
//               <h3 style={{ margin: "4px 0 0", fontSize: "28px", fontWeight: "700", color: "#111827" }}>
//                 {companyList.length}
//               </h3>
//             </div>
//           </div>
//         </div>

//         {/* Search & Filter Bar */}
//         <div
//           style={{
//             background: "white",
//             borderRadius: "12px",
//             padding: "16px",
//             border: "1px solid #e5e7eb",
//             marginBottom: "24px",
//           }}
//         >
//           <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "16px" }}>
//             {/* Search Input */}
//             <div style={{ flex: "1", minWidth: "200px", position: "relative" }}>
//               <FaSearch
//                 size={14}
//                 style={{
//                   position: "absolute",
//                   left: "12px",
//                   top: "50%",
//                   transform: "translateY(-50%)",
//                   color: "#9ca3af",
//                 }}
//               />
//               <input
//                 type="text"
//                 placeholder="Search products..."
//                 value={search}
//                 onChange={(e) => {
//                   setSearch(e.target.value);
//                   setCurrentPage(1);
//                 }}
//                 style={{
//                   width: "100%",
//                   padding: "10px 12px 10px 40px",
//                   border: "2px solid #e5e7eb",
//                   borderRadius: "8px",
//                   fontSize: "14px",
//                   outline: "none",
//                   boxSizing: "border-box",
//                   transition: "all 0.2s",
//                 }}
//                 onFocus={(e) => (e.target.style.borderColor = "#6366f1")}
//                 onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
//               />
//             </div>

//             {/* Filter Toggle Button */}
//             <button
//               onClick={() => setShowFilters(!showFilters)}
//               style={{
//                 background: showFilters ? "#6366f1" : "#f3f4f6",
//                 color: showFilters ? "white" : "#374151",
//                 border: "1px solid" + (showFilters ? "#6366f1" : "#e5e7eb"),
//                 borderRadius: "8px",
//                 padding: "10px 16px",
//                 fontWeight: "600",
//                 cursor: "pointer",
//                 fontSize: "13px",
//                 display: "flex",
//                 alignItems: "center",
//                 gap: "6px",
//                 transition: "all 0.2s",
//               }}
//             >
//               <FaFilter size={12} />
//               Filters
//             </button>
//           </div>

//           {/* Filter Panel */}
//           {showFilters && (
//             <div
//               style={{
//                 background: "#f9fafb",
//                 border: "1px solid #e5e7eb",
//                 borderRadius: "8px",
//                 padding: "16px",
//                 display: "grid",
//                 gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
//                 gap: "12px",
//               }}
//             >
//               {/* Company Filter */}
//               <div>
//                 <label style={{ fontSize: "12px", fontWeight: "600", color: "#374151", display: "block", marginBottom: "6px" }}>
//                   Company
//                 </label>
//                 <select
//                   value={filters.company}
//                   onChange={(e) => {
//                     setFilters({ ...filters, company: e.target.value });
//                     setCurrentPage(1);
//                   }}
//                   style={{
//                     width: "100%",
//                     padding: "8px 12px",
//                     border: "1px solid #d1d5db",
//                     borderRadius: "6px",
//                     fontSize: "13px",
//                     outline: "none",
//                   }}
//                 >
//                   <option value="">All Companies</option>
//                   {companyList.map((c) => (
//                     <option key={c} value={c}>
//                       {c}
//                     </option>
//                   ))}
//                 </select>
//               </div>

//               {/* Quality Filter */}
//               <div>
//                 <label style={{ fontSize: "12px", fontWeight: "600", color: "#374151", display: "block", marginBottom: "6px" }}>
//                   Quality
//                 </label>
//                 <select
//                   value={filters.quality}
//                   onChange={(e) => {
//                     setFilters({ ...filters, quality: e.target.value });
//                     setCurrentPage(1);
//                   }}
//                   style={{
//                     width: "100%",
//                     padding: "8px 12px",
//                     border: "1px solid #d1d5db",
//                     borderRadius: "6px",
//                     fontSize: "13px",
//                     outline: "none",
//                   }}
//                 >
//                   <option value="">All Qualities</option>
//                   {qualityList.map((q) => (
//                     <option key={q} value={q}>
//                       {q}
//                     </option>
//                   ))}
//                 </select>
//               </div>

//               {/* Price Range */}
//               <div>
//                 <label style={{ fontSize: "12px", fontWeight: "600", color: "#374151", display: "block", marginBottom: "6px" }}>
//                   Min Price
//                 </label>
//                 <input
//                   type="number"
//                   placeholder="Min"
//                   value={filters.minPrice}
//                   onChange={(e) => {
//                     setFilters({ ...filters, minPrice: e.target.value });
//                     setCurrentPage(1);
//                   }}
//                   style={{
//                     width: "100%",
//                     padding: "8px 12px",
//                     border: "1px solid #d1d5db",
//                     borderRadius: "6px",
//                     fontSize: "13px",
//                     outline: "none",
//                   }}
//                 />
//               </div>

//               <div>
//                 <label style={{ fontSize: "12px", fontWeight: "600", color: "#374151", display: "block", marginBottom: "6px" }}>
//                   Max Price
//                 </label>
//                 <input
//                   type="number"
//                   placeholder="Max"
//                   value={filters.maxPrice}
//                   onChange={(e) => {
//                     setFilters({ ...filters, maxPrice: e.target.value });
//                     setCurrentPage(1);
//                   }}
//                   style={{
//                     width: "100%",
//                     padding: "8px 12px",
//                     border: "1px solid #d1d5db",
//                     borderRadius: "6px",
//                     fontSize: "13px",
//                     outline: "none",
//                   }}
//                 />
//               </div>

//               {/* Clear Filters */}
//               <div style={{ display: "flex", alignItems: "flex-end" }}>
//                 <button
//                   onClick={() => {
//                     setFilters({ company: "", quality: "", minPrice: "", maxPrice: "" });
//                     setCurrentPage(1);
//                   }}
//                   style={{
//                     width: "100%",
//                     background: "#f3f4f6",
//                     border: "1px solid #d1d5db",
//                     borderRadius: "6px",
//                     padding: "8px 12px",
//                     fontSize: "12px",
//                     fontWeight: "600",
//                     color: "#374151",
//                     cursor: "pointer",
//                     display: "flex",
//                     alignItems: "center",
//                     justifyContent: "center",
//                     gap: "4px",
//                   }}
//                 >
//                   <FaTimesCircle size={12} />
//                   Clear
//                 </button>
//               </div>
//             </div>
//           )}
//         </div>

//         {/* Products Grid */}
//         {currentData.length > 0 ? (
//           <div
//             style={{
//               display: "grid",
//               gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
//               gap: "16px",
//               marginBottom: "24px",
//             }}
//           >
//             {currentData.map((p) => (
//               <div
//                 key={p.id}
//                 style={{
//                   background: "white",
//                   borderRadius: "12px",
//                   border: "1px solid #e5e7eb",
//                   padding: "16px",
//                   transition: "all 0.2s",
//                 }}
//                 onMouseEnter={(e) => {
//                   e.currentTarget.style.boxShadow = "0 10px 25px rgba(0,0,0,0.08)";
//                   e.currentTarget.style.transform = "translateY(-2px)";
//                 }}
//                 onMouseLeave={(e) => {
//                   e.currentTarget.style.boxShadow = "none";
//                   e.currentTarget.style.transform = "translateY(0)";
//                 }}
//               >
//                 <div style={{ marginBottom: "12px" }}>
//                   <h4 style={{ margin: "0 0 4px", fontSize: "15px", fontWeight: "700", color: "#111827" }}>
//                     {p.companyName}
//                   </h4>
//                   <p style={{ margin: "0", fontSize: "12px", color: "#6b7280" }}>
//                     {p.shortName || "N/A"}
//                   </p>
//                 </div>

//                 <div
//                   style={{
//                     display: "grid",
//                     gridTemplateColumns: "1fr 1fr",
//                     gap: "12px",
//                     marginBottom: "12px",
//                     paddingBottom: "12px",
//                     borderBottom: "1px solid #e5e7eb",
//                   }}
//                 >
//                   <div>
//                     <p style={{ margin: "0 0 4px", fontSize: "11px", color: "#6b7280", fontWeight: "500" }}>
//                       CATALOGUE
//                     </p>
//                     <p style={{ margin: "0", fontSize: "13px", fontWeight: "600", color: "#374151" }}>
//                       {p.catalogueName || "-"}
//                     </p>
//                   </div>
//                   <div>
//                     <p style={{ margin: "0 0 4px", fontSize: "11px", color: "#6b7280", fontWeight: "500" }}>
//                       QUALITY
//                     </p>
//                     <p style={{ margin: "0", fontSize: "13px", fontWeight: "600", color: "#374151" }}>
//                       {p.qualityName || "-"}
//                     </p>
//                   </div>
//                   <div>
//                     <p style={{ margin: "0 0 4px", fontSize: "11px", color: "#6b7280", fontWeight: "500" }}>
//                       DESIGN
//                     </p>
//                     <span
//                       style={{
//                         display: "inline-block",
//                         background: "#f3f4f6",
//                         color: "#374151",
//                         padding: "4px 8px",
//                         borderRadius: "6px",
//                         fontSize: "12px",
//                         fontWeight: "600",
//                       }}
//                     >
//                       {p.designNumber}
//                     </span>
//                   </div>
//                   <div>
//                     <p style={{ margin: "0 0 4px", fontSize: "11px", color: "#6b7280", fontWeight: "500" }}>
//                       DATE
//                     </p>
//                     <p style={{ margin: "0", fontSize: "13px", fontWeight: "600", color: "#374151" }}>
//                       {new Date(p.date).toLocaleDateString()}
//                     </p>
//                   </div>
//                 </div>

//                 <div
//                   style={{
//                     display: "flex",
//                     justifyContent: "space-between",
//                     alignItems: "center",
//                   }}
//                 >
//                   <span
//                     style={{
//                       background: "#ecfdf5",
//                       color: "#047857",
//                       padding: "6px 12px",
//                       borderRadius: "6px",
//                       fontSize: "16px",
//                       fontWeight: "bold",
//                     }}
//                   >
//                     ₹{p.rate}
//                   </span>
//                   <button
//                     onClick={() => handleEdit(p)}
//                     style={{
//                       background: "#f3f4f6",
//                       color: "#374151",
//                       border: "1px solid #d1d5db",
//                       borderRadius: "6px",
//                       padding: "6px 12px",
//                       fontWeight: "600",
//                       cursor: "pointer",
//                       fontSize: "12px",
//                       display: "flex",
//                       alignItems: "center",
//                       gap: "4px",
//                       transition: "all 0.2s",
//                     }}
//                     onMouseEnter={(e) => {
//                       e.target.style.background = "#e5e7eb";
//                     }}
//                     onMouseLeave={(e) => {
//                       e.target.style.background = "#f3f4f6";
//                     }}
//                   >
//                     <FaEdit size={11} />
//                     Edit
//                   </button>
//                 </div>
//               </div>
//             ))}
//           </div>
//         ) : (
//           <div
//             style={{
//               background: "white",
//               borderRadius: "12px",
//               border: "1px solid #e5e7eb",
//               padding: "60px 20px",
//               textAlign: "center",
//             }}
//           >
//             <FaBox style={{ fontSize: "48px", color: "#d1d5db", marginBottom: "16px" }} />
//             <p style={{ color: "#6b7280", margin: "0", fontSize: "14px" }}>
//               No products found. Try adjusting your filters or search.
//             </p>
//           </div>
//         )}

//         {/* Pagination */}
//         {totalPages > 1 && (
//           <div
//             style={{
//               display: "flex",
//               justifyContent: "space-between",
//               alignItems: "center",
//               marginTop: "24px",
//               flexWrap: "wrap",
//               gap: "16px",
//             }}
//           >
//             <p style={{ margin: "0", fontSize: "13px", color: "#6b7280" }}>
//               Showing {indexOfFirst + 1}-{Math.min(indexOfLast, filtered.length)} of {filtered.length}
//             </p>
//             <div style={{ display: "flex", gap: "8px" }}>
//               <button
//                 disabled={currentPage === 1}
//                 onClick={() => setCurrentPage(currentPage - 1)}
//                 style={{
//                   background: currentPage === 1 ? "#f3f4f6" : "white",
//                   color: currentPage === 1 ? "#9ca3af" : "#374151",
//                   border: "1px solid #e5e7eb",
//                   borderRadius: "6px",
//                   padding: "8px 12px",
//                   cursor: currentPage === 1 ? "not-allowed" : "pointer",
//                   fontSize: "12px",
//                   fontWeight: "500",
//                 }}
//               >
//                 <FaChevronLeft size={11} />
//               </button>
//               <span
//                 style={{
//                   background: "#6366f1",
//                   color: "white",
//                   padding: "8px 12px",
//                   borderRadius: "6px",
//                   fontWeight: "600",
//                   fontSize: "12px",
//                 }}
//               >
//                 {currentPage} / {totalPages}
//               </span>
//               <button
//                 disabled={currentPage === totalPages}
//                 onClick={() => setCurrentPage(currentPage + 1)}
//                 style={{
//                   background: currentPage === totalPages ? "#f3f4f6" : "white",
//                   color: currentPage === totalPages ? "#9ca3af" : "#374151",
//                   border: "1px solid #e5e7eb",
//                   borderRadius: "6px",
//                   padding: "8px 12px",
//                   cursor: currentPage === totalPages ? "not-allowed" : "pointer",
//                   fontSize: "12px",
//                   fontWeight: "500",
//                 }}
//               >
//                 <FaChevronRight size={11} />
//               </button>
//             </div>
//           </div>
//         )}
//       </main>

//       {/* Modal */}
//       {showModal && (
//         <div
//           style={{
//             position: "fixed",
//             top: 0,
//             left: 0,
//             right: 0,
//             bottom: 0,
//             background: "rgba(0,0,0,0.5)",
//             display: "flex",
//             alignItems: "center",
//             justifyContent: "center",
//             zIndex: 1000,
//             padding: "20px",
//           }}
//           onClick={(e) => {
//             if (e.target === e.currentTarget) setShowModal(false);
//           }}
//         >
//           <div
//             style={{
//               background: "white",
//               borderRadius: "12px",
//               maxWidth: "600px",
//               width: "100%",
//               maxHeight: "90vh",
//               overflowY: "auto",
//             }}
//           >
//             <div
//               style={{
//                 background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
//                 color: "white",
//                 padding: "20px",
//                 display: "flex",
//                 justifyContent: "space-between",
//                 alignItems: "center",
//               }}
//             >
//               <h2 style={{ margin: "0", fontSize: "18px", fontWeight: "700" }}>
//                 {editId ? "✏️ Edit Product" : "➕ Add New Product"}
//               </h2>
//               <button
//                 onClick={() => setShowModal(false)}
//                 style={{
//                   background: "rgba(255,255,255,0.2)",
//                   color: "white",
//                   border: "none",
//                   borderRadius: "6px",
//                   width: "32px",
//                   height: "32px",
//                   cursor: "pointer",
//                   display: "flex",
//                   alignItems: "center",
//                   justifyContent: "center",
//                   fontSize: "16px",
//                 }}
//               >
//                 <FaTimes />
//               </button>
//             </div>

//             <div style={{ padding: "24px" }}>
//               <div
//                 style={{
//                   display: "grid",
//                   gridTemplateColumns: "1fr 1fr",
//                   gap: "16px",
//                 }}
//               >
//                 <div style={{ gridColumn: "1 / -1" }}>
//                   <ComboBox
//                     label="Company Name"
//                     value={form.companyName}
//                     onChange={(val) => setForm({ ...form, companyName: val })}
//                     options={companyList}
//                     placeholder="Select or type company name"
//                     required={true}
//                     icon={FaBuilding}
//                   />
//                 </div>

//                 <ComboBox
//                   label="Short Name"
//                   value={form.shortName}
//                   onChange={(val) => setForm({ ...form, shortName: val })}
//                   options={shortNameList}
//                   placeholder="Select or type short name"
//                 />

//                 <ComboBox
//                   label="Catalogue Name"
//                   value={form.catalogueName}
//                   onChange={(val) => setForm({ ...form, catalogueName: val })}
//                   options={catalogueList}
//                   placeholder="Select or type catalogue"
//                 />

//                 <ComboBox
//                   label="Quality Name"
//                   value={form.qualityName}
//                   onChange={(val) => setForm({ ...form, qualityName: val })}
//                   options={qualityList}
//                   placeholder="Select or type quality"
//                 />

//                 <div>
//                   <label
//                     style={{
//                       display: "block",
//                       marginBottom: "6px",
//                       color: "#374151",
//                       fontWeight: "600",
//                       fontSize: "13px",
//                     }}
//                   >
//                     Design Number <span style={{ color: "#ef4444" }}>*</span>
//                   </label>
//                   <input
//                     type="text"
//                     placeholder="Enter design number"
//                     value={form.designNumber}
//                     onChange={(e) => setForm({ ...form, designNumber: e.target.value })}
//                     style={{
//                       width: "100%",
//                       padding: "10px 12px",
//                       border: "2px solid #d1d5db",
//                       borderRadius: "8px",
//                       fontSize: "13px",
//                       outline: "none",
//                       boxSizing: "border-box",
//                     }}
//                     onFocus={(e) => (e.target.style.borderColor = "#6366f1")}
//                     onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
//                   />
//                 </div>

//                 <div>
//                   <label
//                     style={{
//                       display: "block",
//                       marginBottom: "6px",
//                       color: "#374151",
//                       fontWeight: "600",
//                       fontSize: "13px",
//                     }}
//                   >
//                     Rate (₹)
//                   </label>
//                   <input
//                     type="number"
//                     placeholder="0"
//                     value={form.rate}
//                     onChange={(e) => setForm({ ...form, rate: e.target.value })}
//                     style={{
//                       width: "100%",
//                       padding: "10px 12px",
//                       border: "2px solid #d1d5db",
//                       borderRadius: "8px",
//                       fontSize: "13px",
//                       outline: "none",
//                       boxSizing: "border-box",
//                     }}
//                     onFocus={(e) => (e.target.style.borderColor = "#6366f1")}
//                     onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
//                   />
//                 </div>

//                 <div>
//                   <label
//                     style={{
//                       display: "block",
//                       marginBottom: "6px",
//                       color: "#374151",
//                       fontWeight: "600",
//                       fontSize: "13px",
//                     }}
//                   >
//                     Date
//                   </label>
//                   <input
//                     type="date"
//                     value={form.date}
//                     onChange={(e) => setForm({ ...form, date: e.target.value })}
//                     style={{
//                       width: "100%",
//                       padding: "10px 12px",
//                       border: "2px solid #d1d5db",
//                       borderRadius: "8px",
//                       fontSize: "13px",
//                       outline: "none",
//                       boxSizing: "border-box",
//                     }}
//                     onFocus={(e) => (e.target.style.borderColor = "#6366f1")}
//                     onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
//                   />
//                 </div>
//               </div>
//             </div>

//             <div
//               style={{
//                 padding: "16px 24px",
//                 background: "#f9fafb",
//                 borderTop: "1px solid #e5e7eb",
//                 display: "flex",
//                 gap: "12px",
//                 flexDirection: "row-reverse",
//               }}
//             >
//               <button
//                 onClick={handleSave}
//                 style={{
//                   background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
//                   color: "white",
//                   border: "none",
//                   borderRadius: "8px",
//                   padding: "10px 20px",
//                   fontWeight: "600",
//                   cursor: "pointer",
//                   fontSize: "13px",
//                   display: "flex",
//                   alignItems: "center",
//                   gap: "6px",
//                   flex: 1,
//                 }}
//               >
//                 <FaSave size={12} />
//                 {editId ? "Update" : "Save"}
//               </button>
//               <button
//                 onClick={() => setShowModal(false)}
//                 style={{
//                   background: "#f3f4f6",
//                   color: "#374151",
//                   border: "1px solid #d1d5db",
//                   borderRadius: "8px",
//                   padding: "10px 20px",
//                   fontWeight: "600",
//                   cursor: "pointer",
//                   fontSize: "13px",
//                   flex: 1,
//                 }}
//               >
//                 Cancel
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       <style>{`
//         @keyframes spin {
//           from { transform: rotate(0deg); }
//           to { transform: rotate(360deg); }
//         }
//       `}</style>

//       <Footer />
//     </div>
//   );
// }

// export default ProductPage;



